"use strict";
import PhotoSwipeLightbox from "./photoswipe-lightbox.min.js";
import PhotoSwipe from "./photoswipe.min.js";
import PhotoSwipeVideoPlugin from "./photoswipe-video.js";
import { registerPhotoSwipeDialogA11y } from "./modal-a11y.js";

window.theme = window.theme || {};
const options = {
  templateProduct: ".bls__template-product",
  flashSold: ".bls__flash-sold",
  countdownTimer: ".bls__countdown-timer",
  visitors: ".bls__visitors",
  countdowProduct: ".bls__countdow-product",
  paymentButton: ".bls__product-dynamic-checkout",
  navTabs: ".nav-tabs a",
  accorditionTabs: ".data.accordition",
  productInfomationTab: ".bls__product-tabs-content",
  productInfomationTabLayoutSecond: ".bls__products-tabs",
  tabContent: ".tab-content .tab-item",
  tabContentActive: ".tab-content .tab-item.active",
  productAddons: ".bls__product_addons",
  shareCopy: ".bls__share-copy",
  compareColorsBtn: ".bls__compare-color-btn",
  photoZoomImage: ".bls__zoom-image",
  mediaImage: ".bls__media-image",
};

const updateProductMediaCarouselA11y = (root = document) => {
  root.querySelectorAll(".product__media-gallery").forEach((gallery) => {
    if (!gallery.hasAttribute("role")) {
      gallery.setAttribute("role", "region");
    }
    if (!gallery.hasAttribute("aria-label")) {
      gallery.setAttribute("aria-label", "Product gallery");
    }

    gallery
      .querySelectorAll(".swiper:not(.bls__swiper-gallery-thumbnails):not(.bls__swiper-gallery-thumbnails-mobile)")
      .forEach((carousel) => {
        if (!carousel.hasAttribute("role")) {
          carousel.setAttribute("role", "region");
        }
        if (!carousel.hasAttribute("aria-label")) {
          carousel.setAttribute("aria-label", "Product media carousel");
        }
      });

    gallery.querySelectorAll(".swiper-wrapper").forEach((wrapper) => {
      const slides = Array.from(wrapper.children).filter((slide) =>
        slide.classList.contains("swiper-slide")
      );
      const slideCount = slides.length;

      slides.forEach((slide, index) => {
        if (slide.tagName !== "LI") {
          slide.setAttribute("role", "group");
        } else if (slide.getAttribute("role") === "group") {
          slide.removeAttribute("role");
        }
        slide.setAttribute("aria-label", `Slide ${index + 1} of ${slideCount}`);
      });
    });

    gallery.querySelectorAll(".swiper-pagination-bullet").forEach((bullet, index) => {
      bullet.setAttribute("role", "button");
      bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);
      if (bullet.classList.contains("swiper-pagination-bullet-active")) {
        bullet.setAttribute("aria-current", "true");
      } else {
        bullet.removeAttribute("aria-current");
      }
    });

    gallery.querySelectorAll(".gallery-img").forEach((slide) => {
      const position = slide.getAttribute("data-position");
      const list = slide.closest(".product__media-list");
      const slideCount = list?.querySelectorAll(".gallery-img").length;
      const thumbnailNav = slide.closest("[data-nav-thumb]");
      const slideLabel =
        slide.getAttribute("data-alt") ||
        slide.querySelector("img")?.getAttribute("alt") ||
        "Product media";

      if (!slide.closest(".swiper-slide") && position && slideCount) {
        if (slide.tagName !== "LI") {
          slide.setAttribute("role", "group");
        } else if (slide.getAttribute("role") === "group") {
          slide.removeAttribute("role");
        }
        slide.setAttribute("aria-label", `Slide ${position} of ${slideCount}: ${slideLabel}`);
      }

      if (slide.tagName === "A" && position) {
        slide.setAttribute(
          "aria-label",
          slide.getAttribute("aria-label") || `Jump to slide ${position}: ${slideLabel}`
        );
      }

      if (thumbnailNav && position) {
        slide.setAttribute("role", "button");
        slide.setAttribute("tabindex", "0");
        slide.setAttribute(
          "aria-label",
          slide.getAttribute("aria-label") || `Show slide ${position}: ${slideLabel}`
        );
      }
    });
  });
};

const isProductMediaZoomTrigger = (trigger) => {
  return !!trigger && !(
    trigger.classList.contains("external_video") ||
    trigger.classList.contains("video") ||
    trigger.classList.contains("model")
  );
};

const getProductMediaZoomTriggerLabel = (trigger) => {
  const imageAlt =
    trigger.getAttribute("data-alt") ||
    trigger.querySelector("img")?.getAttribute("alt") ||
    "";

  return imageAlt
    ? `Open ${imageAlt} in zoom modal`
    : "Open product image in zoom modal";
};

const prepareProductMediaZoomTrigger = (trigger) => {
  if (!isProductMediaZoomTrigger(trigger)) {
    return false;
  }

  const label = getProductMediaZoomTriggerLabel(trigger);

  if (trigger.tagName === "BUTTON") {
    trigger.setAttribute("type", "button");
  } else {
    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");
  }

  trigger.setAttribute("aria-label", label);
  return true;
};

theme.ProductVideo = (function () {
  var videos = {};

  var hosts = {
    shopify: "shopify",
    external: "external",
  };

  var selectors = {
    productMediaWrapper: "[data-product-single-media-wrapper]",
  };

  var attributes = {
    enableVideoLooping: "enable-video-looping",
    videoId: "video-id",
  };

  function init(videoContainer, sectionId) {
    if (!videoContainer) {
      return;
    }

    var videoElement = videoContainer.querySelector("iframe, video");

    if (!videoElement) {
      return;
    }

    var mediaId = videoContainer.getAttribute("data-product-media-id");
    videos[mediaId] = {
      mediaId: mediaId,
      sectionId: sectionId,
      host: hostFromVideoElement(videoElement),
      container: videoContainer,
      element: videoElement,
      ready: function () {
        createPlayer(this);
      },
    };

    window.Shopify.loadFeatures([
      {
        name: "video-ui",
        version: "2.0",
        onLoad: setupVideos,
      },
    ]);
    theme.LibraryLoader.load("plyrShopifyStyles");
  }

  function setupVideos(errors) {
    if (errors) {
      fallbackToNativeVideo();
      return;
    }

    loadVideos();
  }

  function createPlayer(video) {
    if (video.player) {
      return;
    }

    var productMediaWrapper = video.container.closest(
      selectors.productMediaWrapper
    );

    var enableLooping =
      productMediaWrapper.getAttribute(
        "data-" + attributes.enableVideoLooping
      ) === "true";

    // eslint-disable-next-line no-undef
    video.player = new Shopify.Video(video.element, {
      loop: { active: enableLooping },
    });

    var pauseVideo = function () {
      if (!video.player) return;
      video.player.pause();
    };

    productMediaWrapper.addEventListener("mediaHidden", pauseVideo);
    productMediaWrapper.addEventListener("xrLaunch", pauseVideo);

    productMediaWrapper.addEventListener("mediaVisible", function () {
      if (theme.Helpers.isTouch()) return;
      if (!video.player) return;
      video.player.play();
    });
  }

  function hostFromVideoElement(video) {
    if (video.tagName === "VIDEO") {
      return hosts.shopify;
    }

    return hosts.external;
  }

  function loadVideos() {
    for (var key in videos) {
      if (videos.hasOwnProperty(key)) {
        var video = videos[key];
        video.ready();
      }
    }
  }

  function fallbackToNativeVideo() {
    for (var key in videos) {
      if (videos.hasOwnProperty(key)) {
        var video = videos[key];

        if (video.nativeVideo) continue;

        if (video.host === hosts.shopify) {
          video.element.setAttribute("controls", "controls");
          video.nativeVideo = true;
        }
      }
    }
  }

  function removeSectionVideos(sectionId) {
    for (var key in videos) {
      if (videos.hasOwnProperty(key)) {
        var video = videos[key];

        if (video.sectionId === sectionId) {
          if (video.player) video.player.destroy();
          delete videos[key];
        }
      }
    }
  }

  return {
    init: init,
    hosts: hosts,
    loadVideos: loadVideos,
    removeSectionVideos: removeSectionVideos,
  };
})();

theme.ProductModel = (function () {
  var modelJsonSections = {};
  var models = {};
  var xrButtons = {};

  var selectors = {
    mediaGroup: "[data-product-single-media-group]",
    xrButton: "[data-shopify-xr]",
  };

  function init(modelViewerContainers, sectionId) {
    modelJsonSections[sectionId] = {
      loaded: false,
    };

    modelViewerContainers.forEach(function (modelViewerContainer, index) {
      var mediaId = modelViewerContainer.getAttribute("data-product-media-id");
      var modelViewerElement =
        modelViewerContainer.querySelector("model-viewer");
      var modelId = modelViewerElement.getAttribute("data-model-id");

      if (index === 0) {
        var mediaGroup = modelViewerContainer.closest(selectors.mediaGroup);
        var xrButton = mediaGroup.querySelector(selectors.xrButton);
        xrButtons[sectionId] = {
          element: xrButton,
          defaultId: modelId,
        };
      }

      models[mediaId] = {
        modelId: modelId,
        sectionId: sectionId,
        container: modelViewerContainer,
        element: modelViewerElement,
      };
    });

    window.Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: setupShopifyXr,
      },
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: setupModelViewerUi,
      },
    ]);
    theme.LibraryLoader.load("modelViewerUiStyles");
  }

  function setupShopifyXr(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", function () {
        setupShopifyXr();
      });
      return;
    }

    for (var sectionId in modelJsonSections) {
      if (modelJsonSections.hasOwnProperty(sectionId)) {
        var modelSection = modelJsonSections[sectionId];

        if (modelSection.loaded) continue;
        var modelJson = document.querySelector("#ModelJson-" + sectionId);

        window.ShopifyXR.addModels(JSON.parse(modelJson.innerHTML));
        modelSection.loaded = true;
      }
    }
    window.ShopifyXR.setupXRElements();
  }

  function setupModelViewerUi(errors) {
    if (errors) return;

    for (var key in models) {
      if (models.hasOwnProperty(key)) {
        var model = models[key];
        if (!model.modelViewerUi) {
          model.modelViewerUi = new Shopify.ModelViewerUI(model.element);
        }
        setupModelViewerListeners(model);
      }
    }
  }

  function setupModelViewerListeners(model) {
    var xrButton = xrButtons[model.sectionId];

    model.container.addEventListener("mediaVisible", function () {
      xrButton.element.setAttribute("data-shopify-model3d-id", model.modelId);
      if (theme.Helpers.isTouch()) return;
      model.modelViewerUi.play();
    });

    model.container.addEventListener("mediaHidden", function () {
      xrButton.element.setAttribute(
        "data-shopify-model3d-id",
        xrButton.defaultId
      );
      model.modelViewerUi.pause();
    });

    model.container.addEventListener("xrLaunch", function () {
      model.modelViewerUi.pause();
    });
  }

  function removeSectionModels(sectionId) {
    for (var key in models) {
      if (models.hasOwnProperty(key)) {
        var model = models[key];
        if (model.sectionId === sectionId) {
          models[key].modelViewerUi.destroy();
          delete models[key];
        }
      }
    }
    delete modelJsonSections[sectionId];
  }

  return {
    init: init,
    removeSectionModels: removeSectionModels,
  };
})();

theme.LibraryLoader = (function () {
  var types = {
    link: "link",
    script: "script",
  };

  var status = {
    requested: "requested",
    loaded: "loaded",
  };

  var cloudCdn = "https://cdn.shopify.com/shopifycloud/";

  var libraries = {
    plyrShopifyStyles: {
      tagId: "plyr-shopify-styles",
      src: cloudCdn + "plyr/v2.0/shopify-plyr.css",
      type: types.link,
    },
    modelViewerUiStyles: {
      tagId: "shopify-model-viewer-ui-styles",
      src: cloudCdn + "model-viewer-ui/assets/v1.0/model-viewer-ui.css",
      type: types.link,
    },
  };

  function load(libraryName, callback) {
    var library = libraries[libraryName];

    if (!library) return;
    if (library.status === status.requested) return;

    callback = callback || function () {};
    if (library.status === status.loaded) {
      callback();
      return;
    }

    library.status = status.requested;

    var tag;

    switch (library.type) {
      case types.script:
        tag = createScriptTag(library, callback);
        break;
      case types.link:
        tag = createLinkTag(library, callback);
        break;
    }

    tag.id = library.tagId;
    library.element = tag;

    var firstScriptTag = document.getElementsByTagName(library.type)[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  function createScriptTag(library, callback) {
    var tag = document.createElement("script");
    tag.src = library.src;
    tag.addEventListener("load", function () {
      library.status = status.loaded;
      callback();
    });
    return tag;
  }

  function createLinkTag(library, callback) {
    var tag = document.createElement("link");
    tag.href = library.src;
    tag.rel = "stylesheet";
    tag.type = "text/css";
    tag.addEventListener("load", function () {
      library.status = status.loaded;
      callback();
    });
    return tag;
  }

  return {
    load: load,
  };
})();

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.setupAccessibleVariantSwatches();
    this.querySelectorAll(".bls__option-swatch").forEach((button) =>
      button.addEventListener("click", this.onVariantChange.bind(this), false)
    );
    if (document.querySelector(".sticky-addcart-opstion")) {
      document.querySelector(".sticky-addcart-opstion").onchange = function(evt){
        var value = evt.target.selectedOptions[0].getAttribute('data-option');
        var arrays = value.split('/');
        arrays.forEach(function(item, key){
          var option = item.trim();
          document.querySelectorAll(".bls__template-main-product fieldset.product-form__input").forEach(function(button, k){
            if (k == key) {
              if (button.querySelector(".bls__option-swatch[data-value='"+option+"']")) {
                button.querySelector(".bls__option-swatch[data-value='"+option+"']").click();
              }
            }
          });
        });
     };
    }
    if (document.querySelector(".product-bought-together-item.main-product")) {
      document.querySelector(".product-bought-together-item.main-product .product-variant-option").onchange = function(evt){
        var value = evt.target.selectedOptions[0].getAttribute('data-option');
        var arrays = value.split('/');
        arrays.forEach(function(item, key){
          var option = item.trim();
          document.querySelectorAll(".bls__template-main-product fieldset.product-form__input").forEach(function(button, k){
            if (k == key) {
              if (button.querySelector(".bls__option-swatch[data-value='"+option+"']")) {
                button.querySelector(".bls__option-swatch[data-value='"+option+"']").click();
              }
            }
          });
        });
     };
    }
  }

  onVariantChange(event) {
    event.preventDefault();
    const target = event.currentTarget;
    const value = target.getAttribute("data-value");
    for (var item of target
      .closest("fieldset")
      .querySelectorAll(".bls__option-swatch")) {
      item.classList.remove("active");
    }
    target.classList.toggle("active");
    target
      .closest("fieldset")
      .querySelector(".swatch-selected-value").textContent = value;
    this.syncAccessibleVariantSwatches();
    const stickyVariantField = document.querySelector('#sticky-variant-name');
    console.log("value", value);
    if (stickyVariantField && value && value != 'Default Title') {
      stickyVariantField.textContent = value;
    }
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();
    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
    }
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll(".bls__option-swatch.active"),
      (select) => select.getAttribute("data-value")
    );
  }

  setupAccessibleVariantSwatches() {
    this.querySelectorAll("fieldset.product-form__input").forEach((fieldset, groupIndex) => {
      const swatches = fieldset.querySelectorAll(".bls__option-swatch");
      if (!swatches.length) return;
      const legendText = (
        fieldset.querySelector(".swatch-attribute-label")?.textContent ||
        `Option ${groupIndex + 1}`
      ).replace(":", "").trim();
      const sectionId = this.dataset.section || this.id || "product";
      const groupName = `variant-${sectionId}-option-${groupIndex + 1}`;
      swatches.forEach((swatch, valueIndex) => {
        const label = swatch.querySelector("label");
        if (!label) return;
        const value = swatch.dataset.value || label.textContent.trim();
        const slug = String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || valueIndex;
        const inputId = `${groupName}-${valueIndex}-${slug}`;
        let input = swatch.querySelector(".bls__option-swatch-input");
        if (!input) {
          input = document.createElement("input");
          input.type = "radio";
          input.className = "visually-hidden bls__option-swatch-input";
          swatch.insertBefore(input, label);
        }
        input.name = groupName;
        input.value = value;
        input.id = inputId;
        input.checked = swatch.classList.contains("active");
        input.disabled = swatch.hasAttribute("data-disabled");
        input.setAttribute("aria-label", `${legendText}: ${value}`);
        const describedBy = fieldset.getAttribute("aria-describedby");
        if (describedBy) {
          input.setAttribute("aria-describedby", describedBy);
        }
        label.setAttribute("for", inputId);
        if (input.dataset.variantRadioReady !== "true") {
          input.dataset.variantRadioReady = "true";
          input.addEventListener("change", () => {
            if (input.checked) {
              this.onVariantChange({ preventDefault() {}, currentTarget: swatch });
            }
          });
        }
      });
    });
  }

  syncAccessibleVariantSwatches() {
    this.querySelectorAll(".bls__option-swatch").forEach((swatch) => {
      const input = swatch.querySelector(".bls__option-swatch-input");
      if (!input) return;
      input.checked = swatch.classList.contains("active");
      input.disabled = swatch.hasAttribute("data-disabled");
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    if (
      document
        .querySelector(".bls__product-images")
        .classList.contains("bls__product-grid-1") ||
      document
        .querySelector(".bls__product-images")
        .classList.contains("bls__product-grid-2")
    ) {
      var target = document.querySelector(
        '.bls__media-image[data-media-id="' +
          `${this.currentVariant.featured_media.id}` +
          '"]'
      );
      var scrollContainer = target;
      do {
        scrollContainer = scrollContainer.parentNode;
        if (!scrollContainer) return;
        scrollContainer.scrollTop += 1;
      } while (scrollContainer.scrollTop == 0);

      var targetY = 0;
      do {
        if (target == scrollContainer) break;
        targetY += target.offsetTop;
      } while ((target = target.offsetParent));

      scroll = function (c, a, b, i) {
        i++;
        if (i > 30) return;
        c.scrollTop = a + ((b - a) / 30) * i;
        setTimeout(function () {
          scroll(c, a, b, i);
        }, 20);
      };
      scroll(scrollContainer, scrollContainer.scrollTop, targetY, 0);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  setAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.dataset.value)) {
        input.removeAttribute("data-disabled");
      } else {
        input.setAttribute("data-disabled", "disable");
      }
    });
    this.syncAccessibleVariantSwatches();
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    if (document.querySelector(".sticky-addcart-opstion")) {
      document.querySelector(".sticky-addcart-opstion").value =
        this.currentVariant.id;
    }
    if (document.querySelector(".product-bought-together-item.main-product")) {
      document.querySelector(".product-bought-together-item.main-product .product-variant-option").value =
        this.currentVariant.id;
    }
    const quantityLabel = document.querySelector(".quantity__label");
    const quantityCart = document.querySelector(".quantity-cart");
    if (quantityLabel) {
      quantityLabel.setAttribute("data-pro-id", this.currentVariant.id);
      if (quantityCart) {
        fetch("/cart.json")
          .then((res) => res.json())
          .then((cart) => {
            const items = cart.items;
            const pro_id = this.currentVariant.id;
            var variant_quantity;
            items.forEach(function(item){
              if (item.variant_id == pro_id) {
                quantityCart.innerHTML = item.quantity;
                quantityLabel.classList.remove("hidden");
                variant_quantity = pro_id;
                return;
              }
            });
            if (!variant_quantity) {
              quantityCart.innerHTML = 0;
              quantityLabel.classList.add("hidden");
            }
          })
          .catch((error) => {
            throw error;
          });
      }
    }
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector("pickup-availability");
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  removeErrorMessage() {
    const section = this.closest("section");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  getVisualDiscountPricing(currentVariant, product) {
    const discountSource =
      product.closest(".bls__template-product") ||
      product.closest(".bls__product-single") ||
      product;
    const enabled =
      discountSource.dataset.globalVisualDiscountEnabled === "true";
    const percent = Number.parseFloat(
      discountSource.dataset.globalVisualDiscountPercent || "0"
    );
    let compare_at_price = currentVariant.compare_at_price;
    let price = currentVariant.price;

    if (enabled && percent > 0) {
      let original_price = compare_at_price;
      if (!original_price || original_price < price) {
        original_price = price;
      }
      compare_at_price = original_price;
      price = Math.round(original_price * ((100 - percent) / 100));
    }

    return { compare_at_price, price };
  }

  renderProductInfo() {
    if (!this.currentVariant) return;
    let qty = 0;
    let percent = 0;
    let sale = false;
    let soldOut = false;
    let variantStrings = window.variantStrings.soldOut;
    let availability = window.variantStrings.inStock;
    const unit_price = this.currentVariant.unit_price;
    const unit_price_measurement = this.currentVariant.unit_price_measurement;
    const form = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    const product = form.closest(".bls__product-details-infor");
    const pricing = this.getVisualDiscountPricing(this.currentVariant, product);
    const compare_at_price = pricing.compare_at_price;
    const price = pricing.price;
    this.getVariantQtyData().find((variantQty) => {
      if (variantQty.id === this.currentVariant.id) {
        qty = variantQty.qty;
      }
    });
    if (compare_at_price && compare_at_price > price) {
      sale = true;
      percent = ((compare_at_price - price) / compare_at_price) * 100;
    }
    const stli = document.querySelector("stock-countdown");
    if (stli) {
      const il = stli.dataset.itemsLeft;
      const ms = stli.dataset.message;
      if (il >= qty && ms && qty >= 1) {
        stli.classList.remove("d-none");
        const qt = stli.querySelector("span.bls__count");
        const progressbar = stli.querySelector(".progressbar-stock");
        let widthProgress;
        if ((qty / il) * 100 > 100) {
          widthProgress = 100;
        } else {
          widthProgress = (qty / il) * 100;
        }
        progressbar.style.width = widthProgress + "%"
        if (qt) {
          qt.innerHTML = qty;
        }
      } else {
        stli.classList.add("d-none");
      }
    }
    const stockNotify = document.querySelector(".product-notify-stock");
    if (stockNotify) {
      if (!this.currentVariant.available) {
        stockNotify.style.display = "block";
        stockNotify.setAttribute(
          "data-product-variant",
          this.currentVariant.id
        );
      } else {
        stockNotify.style.display = "none";
        stockNotify.setAttribute(
          "data-product-variant",
          this.currentVariant.id
        );
      }
    }
    if (unit_price && unit_price_measurement) {
      const price_num = Shopify.formatMoney(
        unit_price,
        cartStrings.money_format
      );
      const price_unit =
        unit_price_measurement.reference_value != 1
          ? unit_price_measurement.reference_value
          : unit_price_measurement.reference_unit;
      const upn = product.querySelector(".unit-price .number");
      const upu = product.querySelector(".unit-price .unit");
      if (upn) {
        upn.innerHTML = price_num;
      }
      if (upu) {
        upu.innerHTML = price_unit;
      }
    }
    if (this.currentVariant.available && qty < 1) {
      availability = window.variantStrings.preOrder;
      variantStrings = window.variantStrings.preOrder;
    } else if (!this.currentVariant.available) {
      availability = window.variantStrings.outStock;
      soldOut = true;
    } else {
      availability = window.variantStrings.inStock;
      variantStrings = window.variantStrings.addToCart;
    }
    if (this.currentVariant.inventory_management === null) {
      soldOut = false;
      availability = window.variantStrings.inStock;
      variantStrings = window.variantStrings.addToCart;
    }
    const product_label = product.querySelector(".bls__product-label");
    if (product_label) {
      product_label.remove();
    }
    if (sale || soldOut) {
      var element = document.createElement("div");
      element.classList.add(
        "bls__product-label",
        "mb-5",
        "fs-12",
        "pointer-events-none",
        "inline-block",
        "static"
      );
     if(product
      .querySelector(".bls__product-label-detail")){
      product
      .querySelector(".bls__product-label-detail")
      .insertBefore(
        element,
        product.querySelector(".bls__product-label-detail").children[0]
      );
     }
      const label = product.querySelector(".bls__product-label");
      var element_lale = document.createElement("div");
      if (sale && soldOut == false) {
        element_lale.classList.add("bls__sale-label");
        if (window.productLabel.saleType == 'price') {
          element_lale.innerText = "- " + Shopify.formatMoney(
            (compare_at_price - price),
            cartStrings.money_format
          );
        } else if (window.productLabel.saleType == 'text') {
          element_lale.innerText = window.productLabel.saleLabel;
        } else {
          element_lale.innerText = -percent.toFixed(0) + "%";
        }
      } else {
        element_lale.classList.add("bls__sold-out-label");
        element_lale.innerText = window.variantStrings.soldOut;
      }

      if (label) {
        label.appendChild(element_lale);
      }
    }
    if (product.querySelector(".bls__availability-value")) {
      product.querySelector(".bls__availability-value").textContent =
        availability;
    }
    if (product.querySelector(".bls__product-sku-value")) {
      product.querySelector(".bls__product-sku-value").textContent =
        this.currentVariant.sku && this.currentVariant.sku != ""
          ? this.currentVariant.sku
          : "N/A";
    }
    const price_format = Shopify.formatMoney(
      price,
      cartStrings.money_format
    );
    if (product.querySelector(".price__regular .price")) {
      product.querySelector(".price__regular .price").innerHTML = price_format;
    }
    const bls__price = product.querySelector(".bls__price");
    if (bls__price) {
      bls__price.classList.remove("price--sold-out", "price--on-sale");
      bls__price
        .querySelector(".price__regular .price")
        .classList.remove("special-price");
      if (compare_at_price && compare_at_price > price) {
        const compare_format = Shopify.formatMoney(
          compare_at_price,
          cartStrings.money_format
        );
        if (product.querySelector(".compare-price")) {
          product.querySelector(".compare-price").innerHTML = `<span class="visually-hidden visually-hidden--inline">Regular price</span>${compare_format}`;
        }
        bls__price.classList.add("price--on-sale");
        product
          .querySelector(".price__regular .price")
          .classList.add("special-price");
      } else if (!this.currentVariant.available) {
        bls__price.classList.add("price--sold-out");
      }
    }
    this.toggleAddButton(!this.currentVariant.available, variantStrings);
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    if (!productForm) return;
    const addButton = productForm.querySelector('.add-cart-button[name="add"]');
    const addButtonText = productForm.querySelector('.add-cart-button[name="add"] > span');
    const buttonPayment = productForm.querySelector(options.paymentButton);
    if (!addButton) return;
    if (disable) {
      addButton.setAttribute("disabled", "disabled");
      if (buttonPayment) {
        buttonPayment.style.display = "none";
      }
    } else {
      addButton.removeAttribute("disabled");
      if (buttonPayment) {
        buttonPayment.style.display = "block";
      }
    }
    if (text) {
      addButtonText.textContent = text;
    } else {
      addButtonText.textContent = window.variantStrings.addToCart;
    }
    if (!modifyClass) return;
  }
  updateVariantStatuses() {
    const selectedOptionOneVariants = this.getVariantData().filter(
      (variant) =>
        this.querySelector(".active").dataset.value === variant.option1
    );

    const inputWrappers = [...this.querySelectorAll(".product-form__input")];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll(".bls__option-swatch")];
      const previousOptionSelected =
        inputWrappers[index - 1].querySelector(".active").dataset.value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          (variant) =>
            variant.available &&
            variant[`option${index}`] === previousOptionSelected
        )
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setUnavailable() {
    const button = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    const addButton = button.querySelector('.add-cart-button[name="add"]');
    const addButtonText = button.querySelector('.add-cart-button[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add("visibility-hidden");
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  getVariantQtyData() {
    this.variantQtyData = JSON.parse(
      this.querySelector(".productVariantsQty").textContent
    );
    return this.variantQtyData;
  }
}
customElements.define("variant-selects", VariantSelects);

var BlsEventMainProductShopify = (function () {
  return {
    init: function () {
      this.setupEventListeners();
      this.eventFlashSold();
      this.eventVisitors();
      this.eventCountdowProduct();
      this.eventProductTabs();
      this.eventCompareColors();
      this.stickyAddToCartButton();
      this.eventMediaGalleryProduct();
      this.eventGalleryJumpLinks();
      this.productMedia();
      this.eventProductBoughTogether();
      this.eventProductGroup();
      this.showPopupStockNotify();
      this.eventSkeleton();
      this.actionDropdownSwatches();
    },

    setupEventListeners: function () {
      const input__product_detail = document.querySelector(
        ".product-detail__input"
      );
      const input__product_sticky = document.querySelector(
        ".product-sticky__input"
      );
      if (input__product_detail && input__product_sticky) {
        input__product_detail.addEventListener("change", (e) => {
          e.preventDefault();
          input__product_sticky.value = input__product_detail.value;
        });
        input__product_sticky.addEventListener("change", (e) => {
          e.preventDefault();
          input__product_detail.value = input__product_sticky.value;
        });
      }
      document.querySelectorAll(options.productAddons).forEach((addons) => {
        addons.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            const target = e.currentTarget;
            const id = target.getAttribute("data-id");
            const text = target.getAttribute("data-text");
            const content = document.getElementById(id);
            let heading = "";
            if (id == "share-popup") {
              heading = " dlg-disable-heading";
            }
            if (content) {
              var promotion = EasyDialogBox.create(
                id,
                "dlg" + heading + " dlg-disable-footer dlg-disable-drag",
                text,
                content.innerHTML
              );
              promotion.onClose = promotion.destroy;
              promotion.show(300);
            }
            if (id == "compare-color-popup") {
              this.eventCompareColors();
            }
            if (id == "share-popup") {
              this.eventCopyShare();
            }
          },
          false
        );
      });

      const prodData = document.querySelector("[data-product-json]");
      if (prodData != null) {
        var productJson = JSON.parse(prodData.innerHTML);
        var storedProducts = JSON.parse(
          localStorage.getItem("bls__recently-viewed-products")
        );
        if (storedProducts) {
          if (storedProducts.length < 25) {
            this.addProductEntry(productJson, storedProducts);
          }
        } else {
          this.addProductEntry(productJson, storedProducts);
        }
      }
    },

    addProductEntry: function (productJson, storedProducts) {
      if (storedProducts === null) storedProducts = [];
      var currentProductID = productJson.id;
      var currentProductHandle = productJson.handle;
      var product = {
        handle: currentProductHandle,
        id: currentProductID,
      };
      if (JSON.parse(localStorage.getItem("product")) != currentProductHandle) {
        localStorage.setItem("product", JSON.stringify(product));
      }
      for (var i = storedProducts.length; i--; ) {
        var curProduct = storedProducts[i];
        if (curProduct.handle === product.handle) {
          storedProducts.splice(i, 1);
          break;
        }
      }
      storedProducts.push(product);
      if (localStorage.getItem(storedProducts)) {
        localStorage.getItem(
          "bls__recently-viewed-products",
          JSON.stringify(storedProducts)
        );
      } else {
        localStorage.setItem(
          "bls__recently-viewed-products",
          JSON.stringify(storedProducts)
        );
      }
    },

    eventGalleryJumpLinks: function () {
      document.addEventListener("click", (event) => {
        const jumpLink = event.target.closest(
          '.product__media-thumbs-left a.gallery-img[href^="#Media-"]'
        );
        if (!jumpLink) return;

        const targetId = jumpLink.getAttribute("href").slice(1);
        const target = document.getElementById(targetId);
        if (!target) return;
        const jumpLinks = jumpLink
          .closest(".product__media-gallery")
          ?.querySelectorAll('.gallery-img[href^="#Media-"]');

        jumpLinks?.forEach((link) => {
          link.removeAttribute("aria-current");
        });
        jumpLink.setAttribute("aria-current", "true");

        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }

        window.setTimeout(() => {
          target.focus({ preventScroll: true });
        }, 0);
      });

      document.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        const thumbnailButton = event.target.closest(
          ".product__media-gallery [data-nav-thumb] .gallery-img[role='button']"
        );

        if (!thumbnailButton) return;

        event.preventDefault();
        thumbnailButton.closest(".swiper-slide")?.click();
      });
    },

    eventMediaGalleryProduct: function () {
      const thumbnails_gallery = document.querySelector(
        ".bls__swiper-gallery-thumbnails"
      );
      const gallery = document.querySelector(".bls__swiper-gallery");
      var thumbnails;
      var thumbnails_mobile;
      var gallery_slide;
      var gallery_slide_mobile;
      if (thumbnails_gallery) {
        if (thumbnails_gallery.classList.contains("bls__swiper-vertical")) {
          var thumbnails = new Swiper(
            ".bls__swiper-gallery-thumbnails.bls__swiper-vertical",
            {
              spaceBetween: 10,
              slidesPerView: 8,
              freeMode: true,
              allowTouchMove: !thumbnails_gallery.dataset.touchmove ? thumbnails_gallery.dataset.touchmove : true,
              direction: "vertical",
              watchSlidesProgress: true,
            }
          );
        } else {
          var thumbnails = new Swiper(".bls__swiper-gallery-thumbnails", {
            spaceBetween: 10,
            slidesPerView: 5,
            freeMode: true,
            watchSlidesProgress: true,
            navigation: {
              nextEl: ".swiper-next",
              prevEl: ".swiper-prev",
            },
          });
        }
      }
      if (gallery) {
        if (thumbnails) {
          var gallery_slide = new Swiper(".bls__swiper-gallery", {
            loop: false,
            speed: 600,
            allowTouchMove: !!gallery.dataset.touchmove ? !gallery.dataset.touchmove : true,
            navigation: {
              nextEl: ".swiper-next",
              prevEl: ".swiper-prev",
            },
            thumbs: {
              swiper: thumbnails,
            },
          });
        } else {
        
          const autoplaying = gallery.dataset.autoplay === "true";
          const itemMobile = gallery?.dataset.mobile;
          const spacing = gallery?.dataset.spacing;
          const row = gallery?.dataset.sectionId;
          var gallery_slide = new Swiper(".bls__swiper-gallery", {
            slidesPerView: itemMobile,
            spaceBetween: Number(spacing),
            autoplay: autoplaying,
            allowTouchMove: !!gallery.dataset.touchmove ? !gallery.dataset.touchmove : true,
            speed: 600,
            loop: false,
            grid: {
              rows: row,
            },
            navigation: {
              nextEl: ".swiper-next",
              prevEl: ".swiper-prev",
            },
            pagination: {
              clickable: true,
              el: ".swiper-pagination",
            },
          });
        }
      }

      const thumbnails_gallery_mobile = document.querySelector(
        ".bls__swiper-gallery-thumbnails-mobile"
      );
      const gallery_mobile = document.querySelector(
        ".bls__swiper-gallery-mobile"
      );

      if (thumbnails_gallery_mobile && gallery_mobile) {
        var thumbnails_mobile = new Swiper(
          ".bls__swiper-gallery-thumbnails-mobile",
          {
            spaceBetween: 10,
            slidesPerView: 4,
            freeMode: true,
            watchSlidesProgress: true,
            navigation: {
              nextEl: ".swiper-next",
              prevEl: ".swiper-prev",
            },
          }
        );
      }

      if (thumbnails_mobile) {
        var gallery_slide_mobile = new Swiper(".bls__swiper-gallery-mobile", {
          spaceBetween: 0,
          speed: 600,
          allowTouchMove: !!gallery_mobile.dataset.touchmove ? !gallery_mobile.dataset.touchmove : true,
          loop: false,
          navigation: {
            nextEl: ".swiper-next",
            prevEl: ".swiper-prev",
          },
          thumbs: {
            swiper: thumbnails_mobile,
          },
        });
      } else {
        var gallery_slide_mobile = new Swiper(".bls__swiper-gallery-mobile", {
          slidesPerView: 1,
          longSwipesMs: 600,
          spaceBetween: 0,
          allowTouchMove: !!gallery_mobile.dataset.touchmove ? !gallery_mobile.dataset.touchmove : true,
          autoplay: false,
          loop: false,
          navigation: {
            nextEl: ".swiper-next",
            prevEl: ".swiper-prev",
          },
          pagination: {
            clickable: true,
            el: ".swiper-pagination",
          },
        });
      }

      const testFlavorMediaGallery = document.querySelector(
        ".product__media-gallery[data-test-flavor-media='true']"
      );
      const useTestFlavorMedia = Boolean(testFlavorMediaGallery);
      const testFlavorImages = useTestFlavorMedia
        ? {
            Vanilla: testFlavorMediaGallery.getAttribute(
              "data-test-flavor-vanilla-image"
            ),
            Chocolate: testFlavorMediaGallery.getAttribute(
              "data-test-flavor-chocolate-image"
            ),
            Unflavored: testFlavorMediaGallery.getAttribute(
              "data-test-flavor-unflavored-image"
            ),
          }
        : {};
      const testFlavorSupplementFactImages = useTestFlavorMedia
        ? {
            Vanilla: "vanilla.png",
            Chocolate: "chock-lit.png",
            Unflavored: "unflavored.png",
          }
        : {};
      const setGalleryMediaImage = (container, imageUrl, altText) => {
        if (!container || !imageUrl) return;

        const safeAltText = altText || "Product image";
        container.setAttribute("data-alt", safeAltText);

        const mediaWrapper = container.querySelector(".bls__media-image");
        if (mediaWrapper) {
          mediaWrapper.setAttribute("data-src", imageUrl);
          mediaWrapper.setAttribute("data-alt", safeAltText);
        }

        const image = container.querySelector("img");
        if (image) {
          image.setAttribute("src", imageUrl);
          image.setAttribute("srcset", imageUrl);
          image.setAttribute("alt", safeAltText);
          image.setAttribute("title", safeAltText);
          image.setAttribute("data-zoom", imageUrl);
        }
      };
      const applyTestFlavorFirstImage = (groupValue) => {
        const imageUrl = testFlavorImages[groupValue];
        if (!imageUrl) return false;

        const selectors = [
          ".product__media-desktop .product__media-list-items > .gallery-img[data-position='1']",
          ".product__media-mobile .swiper-wrapper[data-thumb-feature] > .swiper-slide[data-position='1']",
          ".product__media-thumbs-left .gallery-img[data-position='1']",
          ".product__media-mobile .swiper-wrapper[data-nav-thumb] .gallery-img[data-position='1']",
        ];

        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((container) => {
            setGalleryMediaImage(container, imageUrl, groupValue);
          });
        });

        // Daily Shake stores one Supplement Facts image per flavor in the gallery.
        // Keep only the label that matches the selected flavor visible.
        document
          .querySelectorAll(".product__media-gallery img")
          .forEach((image) => {
            const imageSrc = image.currentSrc || image.getAttribute("src") || "";
            const flavor = Object.entries(testFlavorSupplementFactImages).find(
              ([, filename]) => imageSrc.includes(filename)
            )?.[0];
            if (!flavor) return;

            const mediaItem =
              image.closest(".swiper-slide") || image.closest(".gallery-img");
            if (!mediaItem) return;

            const isSelectedFlavor = flavor === groupValue;
            mediaItem.hidden = !isSelectedFlavor;
            mediaItem.setAttribute("aria-hidden", String(!isSelectedFlavor));

            // Editorial-grid placement normally makes the 12th media full width.
            // Supplement Facts must remain a consistent half-width card for every flavor.
            const galleryImage = image.closest(".gallery-img");
            if (galleryImage) {
              galleryImage.classList.remove("gallery-img--full");
              galleryImage.classList.add("gallery-img--half");
            }
          });

        updateProductMediaCarouselA11y();
        return true;
      };
      //pick add variant group
      var arrSlide = [],
        arrSlideMain = [],
        arrSlideMobile = [],
        arrSlideMainMobile = [],
        arrGalleryImg = [];
      document
        .querySelectorAll(".product__media-desktop [data-group]")
        .forEach((items) => {
          arrSlide.push(items);
        });
      document
        .querySelectorAll(".product__media-desktop [data-group-thumb]")
        .forEach((items) => {
          arrSlideMain.push(items);
        });
      document
        .querySelectorAll(".product__media-mobile [data-group]")
        .forEach((items) => {
          arrSlideMobile.push(items);
        });
      document
        .querySelectorAll(".product__media-mobile [data-group-thumb]")
        .forEach((items) => {
          arrSlideMainMobile.push(items);
        });
      document.querySelectorAll(".gallery-img[data-alt]").forEach((items) => {
        arrGalleryImg.push(items);
      });
      var colorSelected = document
        .querySelector("[data-variant-selected]")
        ?.getAttribute("data-variant-selected");
      if (!colorSelected) {
        colorSelected = document
          .querySelector(".bls__product-details-infor .bls__option-swatch.active")
          ?.getAttribute("data-value");
      }
      if (useTestFlavorMedia) {
        applyTestFlavorFirstImage(colorSelected);
      }
      var checkGroup;
      document.querySelectorAll("[data-alt]").forEach((slide) => {
        var color = slide.getAttribute("data-alt");
        if (colorSelected == color) {
          document
            .querySelector("[data-variant-group]")
            ?.setAttribute("data-variant-group", true);
        }
        checkGroup = document
          .querySelector("[data-variant-group]")
          ?.getAttribute("data-variant-group");
      });
      if (checkGroup === "true") {
        let mainItem = [],
          navItem = [],
          mainItemMobile = [],
          navItemMobile = [];
        arrSlideMain.forEach((items) => {
          var exist = items.getAttribute("data-alt");
          if (exist === colorSelected) {
            mainItem.push(items);
          }
        });
        arrSlide.forEach((items) => {
          var exist = items.getAttribute("data-alt");
          if (exist === colorSelected) {
            navItem.push(items);
          }
        });
        arrSlideMainMobile.forEach((items) => {
          var exist = items.getAttribute("data-alt");
          if (exist === colorSelected) {
            mainItemMobile.push(items);
          }
        });
        arrSlideMobile.forEach((items) => {
          var exist = items.getAttribute("data-alt");
          if (exist === colorSelected) {
            navItemMobile.push(items);
          }
        });
        arrGalleryImg.forEach((items) => {
          var exist = items.getAttribute("data-alt");
          if (exist === colorSelected) {
            items.classList.add("media-active");
            items.classList.remove("media-hidden");
          } else {
            items.classList.add("media-hidden");
            items.classList.remove("media-active");
          }
        });
        if (gallery_slide) {
          gallery_slide.removeAllSlides();
          gallery_slide.appendSlide(mainItem);
          gallery_slide.slideToLoop(0);
          mainItem.forEach((items,index) => {
            items.classList.add("media-active");
            items.setAttribute('data-position',index+1);
            items.querySelector('.gallery-img').setAttribute('data-position',index+1);
          });
          gallery_slide.update();
          this.productMedia();
        }
        if (thumbnails) {
          thumbnails.removeAllSlides();
          thumbnails.appendSlide(navItem);
          thumbnails.slideToLoop(0);
          navItem.forEach((items,index) => {
            items.classList.add("media-active");
            items.setAttribute('data-position',index+1);
            items.querySelector('.gallery-img').setAttribute('data-position',index+1);
          });
          thumbnails.update();
          this.productMedia();
        }
        if (gallery_slide_mobile) {
          gallery_slide_mobile.removeAllSlides();
          gallery_slide_mobile.appendSlide(mainItemMobile);
          gallery_slide_mobile.slideToLoop(0);
          mainItemMobile.forEach((items,index) => {
            items.classList.add("media-active");
            items.setAttribute('data-position',index+1);
            items.querySelector('.gallery-img').setAttribute('data-position',index+1);
          });
          gallery_slide_mobile.update();
          this.productMedia();
        }
        if (thumbnails_mobile) {
          thumbnails_mobile.removeAllSlides();
          thumbnails_mobile.appendSlide(navItemMobile);
          thumbnails_mobile.slideToLoop(0);
          navItemMobile.forEach((items,index) => {
            items.classList.add("media-active");
            items.setAttribute('data-position',index+1);
            items.querySelector('.gallery-img').setAttribute('data-position',index+1);
          });
          thumbnails_mobile.update();
          this.productMedia();
        }
        updateProductMediaCarouselA11y();
      }
      //end
      document
        .querySelectorAll(".bls__product-details-infor .bls__option-swatch")
        .forEach((button) => {
          button.addEventListener(
            "click",
            (e) => {
              const target = e.currentTarget;
              const variantValue = target.getAttribute("data-value");
              if (
                useTestFlavorMedia &&
                applyTestFlavorFirstImage(variantValue)
              ) {
                return;
              }
              //pick add variant group
              var variantColor = target
                .closest("variant-selects")
                .querySelector(".bls__variant-image.active")
                ?.getAttribute("data-filter-color");
              var checkGroup = target
                .closest(".bls__template-product")
                .querySelector("[data-variant-group]")
                ?.getAttribute("data-variant-group");
              let mainItem = [],
                navItem = [],
                mainItemMobile = [],
                navItemMobile = [],
                exist;
              if (checkGroup === "true") {
                arrSlideMain.forEach((items) => {
                  exist = items.getAttribute("data-alt");
                  if (exist === variantColor) {
                    mainItem.push(items);
                  }
                });
                arrSlide.forEach((items) => {
                  exist = items.getAttribute("data-alt");
                  if (exist === variantColor) {
                    if (items.dataset.swiperSlideIndex === "0")
                      items.classList.add("swiper-slide-thumb-active");
                    navItem.push(items);
                  }
                });
                arrSlideMainMobile.forEach((items) => {
                  exist = items.getAttribute("data-alt");
                  if (exist === variantColor) {
                    mainItemMobile.push(items);
                  }
                });
                arrSlideMobile.forEach((items) => {
                  exist = items.getAttribute("data-alt");
                  if (exist === variantColor) {
                    navItemMobile.push(items);
                  }
                });
                arrGalleryImg.forEach((items) => {
                  exist = items.getAttribute("data-alt");
                  if (exist === variantColor) {
                    items.classList.add("media-active");
                    items.classList.remove("media-hidden");
                  } else {
                    items.classList.add("media-hidden");
                    items.classList.remove("media-active");
                  }
                });
                if (gallery_slide) {
                  gallery_slide.removeAllSlides();
                  gallery_slide.appendSlide(mainItem);
                  gallery_slide.slideTo(0);
                  mainItem.forEach((items,index) => {
                    items.classList.add("media-active");
                    items.setAttribute('data-position',index);
                    items.querySelector('.gallery-img').setAttribute('data-position',index+1);
                  });
                  gallery_slide.update();
                  this.productMedia();
                }
                if (thumbnails) {
                  thumbnails.removeAllSlides();
                  thumbnails.appendSlide(navItem);
                  thumbnails.slideTo(0);
                  navItem.forEach((items,index) => {
                    items.classList.add("media-active");
                    items.setAttribute('data-position',index+1);
                    items.querySelector('.gallery-img').setAttribute('data-position',index+1);
                  });
                  thumbnails.update();
                }
                if (gallery_slide_mobile) {
                  gallery_slide_mobile.removeAllSlides();
                  gallery_slide_mobile.appendSlide(mainItemMobile);
                  gallery_slide_mobile.slideTo(0);
                  mainItemMobile.forEach((items,index) => {
                    items.classList.add("media-active");
                    items.setAttribute('data-position',index+1);
                    items.querySelector('.gallery-img').setAttribute('data-position',index+1);
                  });
                  gallery_slide_mobile.update();
                  this.productMedia();
                }
                if (thumbnails_mobile) {
                  thumbnails_mobile.removeAllSlides();
                  thumbnails_mobile.appendSlide(navItemMobile);
                  thumbnails_mobile.slideTo(0);
                  navItemMobile.forEach((items,index) => {
                    items.classList.add("media-active");
                    items.setAttribute('data-position',index+1);
                    items.querySelector('.gallery-img').setAttribute('data-position',index+1);
                  });
                  thumbnails_mobile.update();
                }

                updateProductMediaCarouselA11y();
                BlsZoomThumbsnail.init();
              }
              //end
              if (checkGroup != "true") {
                setTimeout(function () {
                  var options = Array.from(
                    target
                      .closest("variant-selects")
                      .querySelectorAll(".bls__option-swatch.active"),
                    (select) => select.getAttribute("data-value")
                  );
                  var variantData = JSON.parse(
                    target
                      .closest("variant-selects")
                      .querySelector('[type="application/json"]').textContent
                  );
                  var currentVariant = variantData.find((variant) => {
                    return !variant.options
                      .map((option, index) => {
                        return options[index] === option;
                      })
                      .includes(false);
                  });
                  if (!currentVariant) return;
                  if (!currentVariant.featured_media) return;
                  var featured_media_id = currentVariant.featured_media.id;
                  var position = target
                    .closest(".bls__template-product")
                    .querySelector(
                      `.product__media-gallery [data-media-id="${featured_media_id}"]`
                    )
                    .getAttribute("data-position");
                  if (gallery_slide) {
                    gallery_slide.slideTo(position - 1, 600);
                  }
                  if (gallery_slide_mobile) {
                    gallery_slide_mobile.slideTo(position - 1, 600);
                  }
                }, 100);
              }
            },
            false
          );
        });

      if (gallery_slide) {
        gallery_slide.on("slideChange", function () {
          var index = gallery_slide.realIndex + 1;
          var slide = gallery.querySelector(
            `.gallery-img[data-position="${index}"]`
          );
          if (slide?.classList.contains("model")) {
            gallery_slide.allowTouchMove = false;
          } else {
            gallery_slide.allowTouchMove = true;
          }
        });
      }

      gallery_slide_mobile.on("slideChange", function () {
        var index = gallery_slide_mobile.realIndex + 1;
        var slide = gallery_mobile.querySelector(
          `.gallery-img[data-position="${index}"]`
        );
        if (slide?.classList.contains("model")) {
          gallery_slide_mobile.allowTouchMove = false;
        } else {
          gallery_slide_mobile.allowTouchMove = true;
        }
      });

      updateProductMediaCarouselA11y();
    },

    productMedia: function () {
      var position = 0;
      document.querySelectorAll(options.photoZoomImage).forEach((item) => {
        if (!prepareProductMediaZoomTrigger(item) || item.dataset.zoomModalTriggerBound === "true") {
          return;
        }

        const openZoomModal = (e) => {
          var items = this.getProductItems(item);
          e.preventDefault();
          const target = e.currentTarget;
          if (!isProductMediaZoomTrigger(target)) {
            return;
          }
          if (target.getAttribute("data-position")) {
            position = target.getAttribute("data-position");
          } else if (
            target
              .closest(".product__media-list")
              .querySelector(".swiper-slide-active")
          ) {
            position = target
              .closest(".product__media-list")
              .querySelector(".swiper-slide-active")
              .getAttribute("data-position");
          } else if (target.closest(".product__media-list").firstChild) {
            position = target
              .closest(".product__media-list")
              .firstChild?.getAttribute("data-position");
          }
          const options = {
            dataSource: items,
            counter: false,
            zoom: false,
            preloader: false,
            arrowPrev: false,
            arrowNext: false,
            close: false,
            loop: false,
            bgOpacity: 1,
            pswpModule: PhotoSwipe,
          };
          const lightbox = new PhotoSwipeLightbox(options);
          registerPhotoSwipeDialogA11y(lightbox, { label: "Product media dialog", closeLabel: "Close dialog" });
          lightbox.on("contentAppendImage", ({ content }) => {
            if (content.element && content.data?.alt) {
              content.element.setAttribute("title", content.data.alt);
            }
          });
          lightbox.on("uiRegister", function () {
            lightbox.pswp.ui.registerElement({
              name: "bls--close",
              isButton: true,
              tagName: "button",
              title: "Close dialog",
              html: '<i class="icon-x" aria-hidden="true"></i>',
              onClick: () => {
                const pswp = lightbox.pswp;
                pswp.close();
              },
            });
            lightbox.pswp.ui.registerElement({
              name: "bottomBar",
              className: "pswp__bottom-bar",
              appendTo: "wrapper",
              onInit: (el, pswp) => {
                let next, prev, counter;
                let iconNext, iconPrev;
                next = document.createElement("button");
                next.setAttribute("type", "button");
                next.setAttribute("aria-label", "Next slide");
                next.className = "pswp__button pswp__button-next";
                iconNext = document.createElement("i");
                iconNext.className = "icon-chevron-right";
                iconNext.setAttribute("aria-hidden", "true");
                next.appendChild(iconNext);
                next.onclick = () => {
                  pswp.next();
                };
                el.appendChild(next);

                counter = document.createElement("span");
                counter.className = "pswp__counter";
                counter.setAttribute("aria-live", "polite");
                pswp.on("change", () => {
                  counter.innerText = `${pswp.currIndex + 1}${pswp.options.indexIndicatorSep}${pswp.getNumItems()}`;
                  counter.setAttribute(
                    "aria-label",
                    `Slide ${pswp.currIndex + 1} of ${pswp.getNumItems()}`
                  );
                });
                el.appendChild(counter);

                prev = document.createElement("button");
                prev.setAttribute("type", "button");
                prev.setAttribute("aria-label", "Previous slide");
                prev.className = "pswp__button pswp__button-prev";
                iconPrev = document.createElement("i");
                iconPrev.className = "icon-chevron-left";
                iconPrev.setAttribute("aria-hidden", "true");
                prev.appendChild(iconPrev);
                prev.onclick = () => {
                  pswp.prev();
                };
                el.appendChild(prev);
              },
            });
            lightbox.pswp.ui.registerElement({
              name: "nutritionLabelText",
              className: "visually-hidden pswp__nutrition-label-text",
              appendTo: "wrapper",
              onInit: (el, pswp) => {
                const updateNutritionText = () => {
                  const textAlternative =
                    pswp.currSlide?.data?.textAlternative ||
                    pswp.currSlide?.data?.nutritionText ||
                    "";
                  el.innerHTML = textAlternative;
                };

                pswp.on("change", updateNutritionText);
                updateNutritionText();
              },
            });
          });
          lightbox.loadAndOpen(position - 1);
          const videoPlugin = new PhotoSwipeVideoPlugin(lightbox, {});
          lightbox.init();
        };

        item.dataset.zoomModalTriggerBound = "true";
        item.addEventListener("click", openZoomModal, false);
        item.addEventListener("keydown", (event) => {
          if (item.tagName === "BUTTON") {
            return;
          }

          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            item.click();
          }
        });
      });

      var sectionId = document.querySelector(".bls__template-product")?.getAttribute("data-section");
      var productMediaTypeVideoDesktop = document.querySelectorAll(
        "[data-product-media-type-video-desktop]"
      );
      productMediaTypeVideoDesktop.forEach(function (el) {
        theme.ProductVideo.init(el, sectionId);
      });

      var productMediaTypeVideoMobile = document.querySelectorAll(
        "[data-product-media-type-video-mobile]"
      );
      productMediaTypeVideoMobile.forEach(function (el) {
        theme.ProductVideo.init(el, sectionId);
      });

      var modelViewerElementsDesktop = document.querySelectorAll(
        "[data-product-media-type-model-desktop]"
      );
      if (modelViewerElementsDesktop.length >= 1) {
        theme.ProductModel.init(modelViewerElementsDesktop, sectionId);
      }

      var modelViewerElementsMobile = document.querySelectorAll(
        "[data-product-media-type-model-mobile]"
      );
      if (modelViewerElementsMobile.length >= 1) {
        theme.ProductModel.init(modelViewerElementsMobile, sectionId);
      }
    },

    getProductItems: function (item) {
      var items = [];
      if (
        item.closest(".product__media-list").querySelectorAll(".media-active")
          .length > 0
      ) {
        item
          .closest(".product__media-list")
          .querySelectorAll(".media-active " + options.mediaImage)
          .forEach((img) => {
            var $this = img,
              width = $this.getAttribute("data-width"),
              height = $this.getAttribute("data-height");
            if ($this.classList.contains("video")) {
              var source = $this.getAttribute("data-source-url");
              var media = $this.getAttribute("data-src");
              items.push({
                videoSrc: source,
                type: "video",
                width: width,
                height: height,
                msrc: media,
              });
            } else if ($this.classList.contains("external_video")) {
              var data_source = $this.getAttribute("data-source");
              var data_source_id = $this.getAttribute("data-video-id");
              var source = "youtube";
              var source_url = "";
              if (data_source == "youtube") {
                source = "youtube";
                source_url =
                  "https://www.youtube.com/watch?v=" + data_source_id + "";
              } else {
                source = "vimeo";
                source_url = "https://vimeo.com/" + data_source_id + "";
              }
              items.push({
                videoSrc: source_url,
                type: "video",
                width: width,
                height: height,
              });
            } else if (!$this.classList.contains("model")) {
              var src = $this.getAttribute("data-src");
              var alt =
                $this.getAttribute("data-alt") ||
                $this.querySelector("img")?.getAttribute("alt") ||
                "Product image";
              var nutritionText =
                $this.querySelector("[data-nutrition-label-text]")?.innerHTML ||
                "";
              var textAlternative =
                $this.querySelector("[data-product-media-text-alternative]")?.innerHTML ||
                nutritionText;
              items.push({
                src: src,
                width: width,
                height: height,
                alt: alt,
                nutritionText: nutritionText,
                textAlternative: textAlternative,
              });
            }
          });
      } else {
        item
          .closest(".product__media-list")
          .querySelectorAll(options.mediaImage)
          .forEach((img) => {
            var $this = img,
              width = $this.getAttribute("data-width"),
              height = $this.getAttribute("data-height");
            if ($this.classList.contains("video")) {
              var source = $this.getAttribute("data-source-url");
              var media = $this.getAttribute("data-src");
              items.push({
                videoSrc: source,
                type: "video",
                width: width,
                height: height,
                msrc: media,
              });
            } else if ($this.classList.contains("external_video")) {
              var data_source = $this.getAttribute("data-source");
              var data_source_id = $this.getAttribute("data-video-id");
              var source = "youtube";
              var media = $this.getAttribute("data-src");
              var source_url = "";
              if (data_source == "youtube") {
                source = "youtube";
                source_url =
                  "https://www.youtube.com/watch?v=" + data_source_id + "";
              } else {
                source = "vimeo";
                source_url = "https://vimeo.com/" + data_source_id + "";
              }
              items.push({
                videoSrc: source_url,
                type: "video",
                width: width,
                height: height,
                msrc: media
              });
            } else if (!$this.classList.contains("model")) {
              var src = $this.getAttribute("data-src");
              var alt =
                $this.getAttribute("data-alt") ||
                $this.querySelector("img")?.getAttribute("alt") ||
                "Product image";
              var nutritionText =
                $this.querySelector("[data-nutrition-label-text]")?.innerHTML ||
                "";
              var textAlternative =
                $this.querySelector("[data-product-media-text-alternative]")?.innerHTML ||
                nutritionText;
              items.push({
                src: src,
                width: width,
                height: height,
                alt: alt,
                nutritionText: nutritionText,
                textAlternative: textAlternative,
              });
            }
          });
      }

      return items;
    },

    eventCopyShare: function () {
      document.querySelectorAll(options.shareCopy).forEach((action) => {
        action.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            const target = e.currentTarget;
            var text = document.getElementById("share-popup-input");
            text.select();
            text.setSelectionRange(0, 99999);
            navigator.clipboard.writeText(text.value);
            target.classList.add("active");
          },
          false
        );
      });
    },

    eventCompareColors: function () {
      document.querySelectorAll(options.compareColorsBtn).forEach((compare) => {
        compare.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            const target = e.currentTarget;
            const color = target.getAttribute("data-color");
            const image = target.getAttribute("data-image");
            const ratio = target.getAttribute("data-ratio");
            const slugify = (str) =>
              str
                .trim()
                .replace(/[^\w\s-]/g, "")
                .replace(/[\s_-]+/g, "-")
                .replace(/^-+|-+$/g, "");
            if (target.classList.contains("active")) {
              target.classList.remove("active");
              document
                .querySelector(`.dlg .item-compare-color${slugify(color)}`)
                .remove();
              setTimeout(function () {
                if (!document.querySelector(".dlg .item-compare-color")) {
                  document
                    .querySelector(".dlg .compare-color-empty")
                    .classList.remove("d-none");
                }
              }, 100);
            } else {
              document
                .querySelector(".dlg .compare-color-empty")
                .classList.add("d-none");
              target.classList.add("active");
              if (color && image) {
                const html =
                  '<div class="image" style="--aspect-ratio: ' +
                  ratio +
                  '"><img src="' +
                  image +
                  '" alt="' +
                  color +
                  '"></div><p class="mt-10 heading-color capitalize">' +
                  color +
                  "</p>";
                const el = document.createElement("div");
                el.classList.add(`item-compare-color${slugify(color)}`);
                el.innerHTML = html;
                document
                  .querySelector(".dlg .bls__compare-colors-list")
                  .appendChild(el);
              }
            }
          },
          false
        );
      });
    },

    eventProductTabs: function () {
      document.querySelectorAll(options.navTabs).forEach((tabToggle) => {
        tabToggle.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            const target = e.currentTarget;
            const tab_id = target.getAttribute("data-block-id");
            if (!target.closest(".data.item").classList.contains("active")) {
              for (var item of document.querySelectorAll(".data.item")) {
                item.classList.remove("active");
              }
              for (var item of document.querySelectorAll(options.tabContent)) {
                item.classList.remove("active");
                item.querySelector(".tab-panel").style.display = "none";
              }
              const conditions = document.getElementById(tab_id);
              conditions.classList.add("active");
              conditions.querySelector(".tab-panel").style.display = "block";
              target.closest(".data.item").classList.add("active");
            }
          },
          false
        );
      });

      document
        .querySelectorAll(options.accorditionTabs)
        .forEach((tabToggle) => {
          tabToggle.addEventListener(
            "click",
            (e) => {
              e.preventDefault();
              const target = e.currentTarget;
              const tab_id = target.getAttribute("data-block-id");
              const conditions = document.getElementById(tab_id);
              if (!conditions) return;
              if (conditions.classList.contains("active")) {
                slideAnime({
                  target: conditions.querySelector(".tab-panel"),
                  animeType: "slideUp",
                });
                conditions.classList.remove("active");
              } else {
                for (var item of document.querySelectorAll(
                  options.tabContentActive
                )) {
                  if (item.classList.contains("active")) {
                    item.classList.remove("active");
                    slideAnimeHidden({
                      target: item.querySelector(".tab-panel"),
                    });
                  }
                }
                slideAnime({
                  target: conditions.querySelector(".tab-panel"),
                  animeType: "slideDown",
                });
                conditions.classList.add("active");
              }
            },
            false
          );
        });

      if (document.querySelector(".inside-product-main-infomation")) {
        const el = document.querySelector(options.productInfomationTab);
        el.classList.remove("hidden");
        if (document.querySelector(".bls__template-product-2")) {
          document
            .querySelector(".bls__template-product-2")
            .classList.add("inside-product-main-infomation-layout2");
        }
        setTimeout(function () {
          el.querySelector(".loading").remove();
          el.appendChild(
            document.querySelector(
              ".inside-product-main-infomation .product.info.detailed"
            )
          );
        }, 500);
      }
      if (window.innerWidth >= 992) {
        const checkTab = document.querySelector(
          ".inside-product-main-infomation"
        );
        if (document.querySelector(".bls__template-product-2") && !checkTab) {
          const el = document.querySelector(
            options.productInfomationTabLayoutSecond
          );
          if (document.querySelector(".design-accordition")) {
            el.classList.add("design-accordition");
          } else if (document.querySelector(".design-show_all")) {
            el.classList.add("design-show_all");
          }
          setTimeout(function () {
            el.querySelector(".loading").remove();
            el.classList.remove("hidden");
            el.appendChild(document.querySelector(".product.info.detailed"));
          }, 500);
        }
      }
    },

    eventFlashSold: function () {
      Shopify.eventFlashSold();
    },

    eventVisitors: function () {
      const element = document.querySelector(options.visitors);
      if (!element) return;
      const max = Number(element.getAttribute("data-count-range-max"));
      const min = Number(element.getAttribute("data-count-range-min"));
      const view_duration = Number(element.getAttribute("data-view-duration"));
      var visitors = Math.floor(Math.random() * (max - min + 1)) + min;
      document.querySelector(".bls__visitor-count").innerHTML = visitors;
      setInterval(function () {
        var visitors = Math.floor(Math.random() * (max - min + 1)) + min;
        document.querySelector(".bls__visitor-count").innerHTML = visitors;
      }, view_duration * 1000);
      element.style.display = "block";
    },
                  
    stickyAddToCartButton: function () {
      const initStickyAddToCart = function () {
        var productCartButton = document.querySelector(".product-form__submit"),
          stickyAddToCartButton = document.querySelector(
            "#bls__sticky-addcart"
          );
        if (!productCartButton || !stickyAddToCartButton) {
          return;
        }
        if (
          stickyAddToCartButton &&
          stickyAddToCartButton.classList.contains("only-show-desktop")
        ) {
          if (window.innerWidth >= 992) {
            document.body.classList.add("sticky-addtocart-show");
          }
        } else {
          document.body.classList.remove("sticky-addtocart-show");
        }
        const syncStickyState = function () {
          const showstickyAddToCartButton =
            productCartButton.getBoundingClientRect().bottom < 0;

          if (showstickyAddToCartButton) {
            stickyAddToCartButton.classList.add("sticky-addcart-show");
            document.querySelector("body").classList.add("sticky-addcart-show");
          } else {
            stickyAddToCartButton.classList.remove("sticky-addcart-show");
            document.querySelector("body").classList.remove("sticky-addcart-show");
          }
        };

        syncStickyState();
        window.addEventListener("scroll", syncStickyState, { passive: true });
        window.addEventListener("resize", syncStickyState);
      };
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initStickyAddToCart, { once: true });
      } else {
        initStickyAddToCart();
      }
    },

    eventCountdowProduct: function () {
      const element = document.querySelector(options.countdowProduct);
      if (!element) return;
      const dataProductID = document
        .querySelector(options.templateProduct)
        .getAttribute("data-product-id");
      fetch("/admin/orders.json")
        .then((res) => res.json())
        .then((orders) => {
          var sum = 0;
          var orders = orders.orders;
          orders.forEach(function (item, index) {
            item.line_items.forEach(function (itemi, index) {
              if (itemi.product_id == dataProductID) {
                var num = itemi.quantity;
                sum += num || 0;
              }
            });
          });
          var variants = parseInt(
            element.querySelector(".bls__count").innerText
          );
          var count = variants + sum;
          var a = 100 * (variants / count);
          setTimeout(function () {
            element.querySelector(".progressbar div").style.width = a + "%";
          }, 300);
        })
        .catch((error) => {
          throw error;
        });
      setInterval(function () {
        fetch("/admin/orders.json")
          .then((res) => res.json())
          .then((orders) => {
            var sumqt = 0;
            var orders = orders.orders;
            orders.forEach(function (item, index) {
              item.line_items.forEach(function (itemi, index) {
                if (itemi.product_id == dataProductID) {
                  var num = itemi.quantity;
                  sumqt += num || 0;
                }
              });
            });
            var sumqty = 0;
            fetch("/admin/products/" + dataProductID + "/variants.json")
              .then((res) => res.json())
              .then((variant) => {
                var variants = variant.variants;
                variants.forEach(function (itemvariant, index) {
                  var numqty = itemvariant.inventory_quantity;
                  sumqty += numqty || 0;
                });
                element.querySelector(".bls__count").innerHTML = sumqty;
              })
              .catch((error) => {
                throw error;
              });
            var variants = parseInt(
              element.querySelector(".bls__count").innerText
            );
            var count = variants + sumqt;
            var a = 100 * (variants / count);
            setTimeout(function () {
              element.querySelector(".progressbar div").style.width = a + "%";
            }, 300);
          })
          .catch((error) => {
            throw error;
          });
      }, 10000);
    },

    eventProductBoughTogether: function () {
      var boughTogether = document.querySelector(
        '.productBoughTogether[type="application/json"]'
      );
      if (!boughTogether) return;
      var variantData = JSON.parse(boughTogether.innerText);
      var query = "";
      variantData.forEach((e, key, variantData) => {
        if (!Object.is(variantData.length - 1, key)) {
          query += e + "%20OR%20id:";
        } else {
          query += e;
        }
      });
      var productAjaxURL =
        "?q=id:" + query + "&section_id=product-bough-together";
      fetch(`${window.routes.search_url}${productAjaxURL}`)
        .then((response) => response.text())
        .then(async (responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          document.getElementById("bls__product-bought-together").innerHTML =
            html.querySelector(".bls__bought-together").innerHTML;
        })
        .catch((e) => {
          throw error;
        })
        .finally(() => {
          this.eventProductBoughTogetherAction();
          BlsLazyloadImg.init();
        });
    },

    eventProductBoughTogetherAction: function () {
      var _this = this;
      document
        .querySelectorAll(".bought-together-checkbox")
        .forEach((checkbox) => {
          checkbox.addEventListener(
            "change",
            (event) => {
              var target = event.target;
              var total_price = 0,
                total_compare_price = 0,
                save_price = 0,
                price,
                compare_price,
                pro_handle = event.target.getAttribute("data-handle");
              var img = target
                .closest("#bls__product-bought-together")
                .querySelector(".product-bought-image-item." + pro_handle + "");
              if (target.checked) {
                img.classList.add("select");
                target
                  .closest(".product-bought-together-item")
                  .classList.add("select");
                target
                  .closest(".product-bought-together-item")
                  .querySelector(".product-variant-option")
                  .removeAttribute("disabled");
                target
                  .closest(".product-bought-together-item")
                  .querySelector(".quantity")
                  .removeAttribute("disabled");
              } else {
                img.classList.remove("select");
                target
                  .closest(".product-bought-together-item")
                  .classList.remove("select");
                target
                  .closest(".product-bought-together-item")
                  .querySelector(".product-variant-option")
                  .setAttribute("disabled", true);
                target
                  .closest(".product-bought-together-item")
                  .querySelector(".quantity")
                  .setAttribute("disabled", true);
              }
              setTimeout(function () {
                var bought_together_select = document.querySelectorAll(
                  ".product-bought-together-item.select"
                );
                bought_together_select.forEach((item) => {
                  var option = item.querySelector(".product-variant-option");
                  price = option.getAttribute("data-price");
                  compare_price = option.getAttribute("data-compare-price");
                  total_price = total_price + Number(price);
                  total_compare_price =
                    total_compare_price + Number(compare_price);
                });
                save_price = total_compare_price - total_price;
                _this.eventProductBoughTogetherUpdatePrice(
                  total_price,
                  total_compare_price,
                  save_price
                );
                if (bought_together_select.length <= 1) {
                  document
                    .querySelector(".bought-together-submit")
                    .setAttribute("disabled", true);
                } else {
                  document
                    .querySelector(".bought-together-submit")
                    .removeAttribute("disabled");
                }
              }, 50);
            },
            false
          );
        });
      document
        .querySelectorAll(
          "#bls__product-bought-together .product-variant-option"
        )
        .forEach((select) => {
          select.addEventListener(
            "change",
            (event) => {
              var target = event.target;
              var total_price = 0,
                total_compare_price = 0,
                save_price = 0,
                image =
                  target.options[target.selectedIndex].getAttribute(
                    "data-image"
                  ),
                price =
                  target.options[target.selectedIndex].getAttribute(
                    "data-price"
                  ),
                pro_handle = target.getAttribute("data-handle"),
                compare_price =
                  target.options[target.selectedIndex].getAttribute(
                    "data-compare-price"
                  );
              var img = target
                .closest("#bls__product-bought-together")
                .querySelector(".product-bought-image-item." + pro_handle + "")
                .querySelector("img");
              if (img) {
                img.removeAttribute("srcset");
                img.setAttribute("src", image);
              }
              var info_price = target
                .closest(".product-bought-together-item")
                .querySelector(".info-price");
              info_price.querySelector(".price__regular .price").innerHTML =
                Shopify.formatMoney(price, cartStrings.money_format);
              const bls__price = info_price.querySelector(".bls__price");
              bls__price.classList.remove("price--sold-out", "price--on-sale");
              bls__price
                .querySelector(".price__regular .price")
                .classList.remove("special-price");
              if (compare_price && compare_price > price) {
                const compare_format = Shopify.formatMoney(
                  compare_price,
                  cartStrings.money_format
                );
                if (bls__price.querySelector(".compare-price")) {
                  bls__price.querySelector(".compare-price").innerHTML =
                    `<span class="visually-hidden visually-hidden--inline">Regular price</span>${compare_format}`;
                }
                bls__price.classList.add("price--on-sale");
                bls__price
                  .querySelector(".price__regular .price")
                  .classList.add("special-price");
              }
              target.setAttribute("data-price", price);
              target.setAttribute("data-compare-price", compare_price);
              document
                .querySelectorAll(".product-bought-together-item.select")
                .forEach((item) => {
                  var option = item.querySelector(".product-variant-option");
                  price = option.getAttribute("data-price");
                  compare_price = option.getAttribute("data-compare-price");
                  total_price = total_price + Number(price);
                  total_compare_price =
                    total_compare_price + Number(compare_price);
                });
              save_price = total_compare_price - total_price;
              _this.eventProductBoughTogetherUpdatePrice(
                total_price,
                total_compare_price,
                save_price
              );
            },
            false
          );
        });

      document.querySelectorAll(".bought-together-submit").forEach((button) => {
        button.addEventListener(
          "click",
          this.submitBoughtTogether.bind(this),
          false
        );
      });
    },

    submitBoughtTogether: function (event) {
      event.preventDefault();
      const target = event.currentTarget;
      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      const form = document.getElementById("bls__bought-together-form");
      const config = fetchConfig("json");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      const formData = new FormData(form);
      if (cart) {
        formData.append(
          "sections",
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
      }
      config.body = formData;
      target.classList.add("btn-loading");
      fetch(`${routes.cart_add_url}.js`, config)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          fetch("/cart.json")
            .then((res) => res.json())
            .then((cart) => {
              if (cart.item_count != undefined) {
                window.updateCartCountBadges(cart.item_count);
                if (document.querySelector("header-total-price")) {
                  document
                    .querySelector("header-total-price")
                    .updateTotal(cart);
                }
                const cart_free_ship = document.querySelector(
                  "free-ship-progress-bar"
                );
                if (cart_free_ship) {
                  cart_free_ship.init(cart.items_subtotal_price);
                }
              }
            })
            .catch((error) => {
              throw error;
            });
          const parsedState = JSON.parse(state);
          cart.getSectionsToRender().forEach((section) => {
            const elementToReplace = document.getElementById(section.id);
            const html = new DOMParser().parseFromString(
              parsedState.sections[section.id],
              "text/html"
            );
            elementToReplace.innerHTML =
              html.querySelector("#form-mini-cart").innerHTML;
            const countdown = cart.querySelector(".cart-countdown-time");
            const html_countdown = html.querySelector(".cart-countdown-time");
            if (countdown && html_countdown) {
              countdown.innerHTML = html_countdown.innerHTML;
              cart.countdownTimer();
            }
          });
          cart.cartAction();
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {
          target.classList.remove("btn-loading");
          cart.open();
          BlsLazyloadImg.init();
        });
    },

    eventProductBoughTogetherUpdatePrice: function (
      total_price,
      total_compare_price,
      save_price
    ) {
      var total = document.querySelector(
        ".bought-together-container .total-product"
      );
      if (total) {
        total.querySelector(".saved-price .price").innerHTML =
          Shopify.formatMoney(save_price, cartStrings.money_format);
        total.querySelector(
          ".bls__total-price .price__sale .price-item"
        ).innerHTML = Shopify.formatMoney(
          total_compare_price,
          cartStrings.money_format
        );
        total.querySelector(".bls__total-price .price").innerHTML =
          Shopify.formatMoney(total_price, cartStrings.money_format);
        if (total_compare_price > total_price) {
          total
            .querySelector(".bls__total-price")
            .classList.add("price--on-sale");
          total
            .querySelector(".bls__total-price .price")
            .classList.add("special-price");
        } else {
          total
            .querySelector(".bls__total-price")
            .classList.remove("price--on-sale");
          total
            .querySelector(".bls__total-price .price")
            .classList.remove("special-price");
        }
        if (save_price > 1) {
          total.querySelector(".saved-price").classList.add("d-block");
          total.querySelector(".saved-price").classList.remove("d-none");
        } else {
          total.querySelector(".saved-price").classList.add("d-none");
          total.querySelector(".saved-price").classList.remove("d-block");
        }
      }
    },

    eventProductGroup: function () {
      var group = document.querySelector(
        '.productGroup[type="application/json"]'
      );
      if (!group) return;
      var variantData = JSON.parse(group.innerText);
      var query = "";
      variantData.forEach((e, key, variantData) => {
        if (!Object.is(variantData.length - 1, key)) {
          query += e + "%20OR%20id:";
        } else {
          query += e;
        }
      });
      var productAjaxURL = "?q=id:" + query + "&section_id=product-grouped";
      fetch(`${window.routes.search_url}${productAjaxURL}`)
        .then((response) => response.text())
        .then(async (responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          document.getElementById("bls__product-group").innerHTML =
            html.querySelector(".bls__product-group").innerHTML;
        })
        .catch((e) => {
          throw error;
        })
        .finally(() => {
          this.eventProductGroupAction();
          BlsLazyloadImg.init();
        });
    },

    eventProductGroupAction: function () {
      document
        .querySelectorAll("#bls__product-group .product-variant-option")
        .forEach((select) => {
          select.addEventListener(
            "change",
            (event) => {
              var target = event.target;
              var image =
                  target.options[target.selectedIndex].getAttribute(
                    "data-image"
                  ),
                price =
                  target.options[target.selectedIndex].getAttribute(
                    "data-price"
                  ),
                pro_handle = target.getAttribute("data-handle"),
                compare_price =
                  target.options[target.selectedIndex].getAttribute(
                    "data-compare-price"
                  );
              var img = target
                .closest("#bls__product-group")
                .querySelector(".product-group-image-item." + pro_handle + "")
                .querySelector("img");
              if (img) {
                img.removeAttribute("srcset");
                img.setAttribute("src", image);
              }
              var info_price = target
                .closest(".product-group-item")
                .querySelector(".info-price");
              info_price.querySelector(".price__regular .price").innerHTML =
                Shopify.formatMoney(price, cartStrings.money_format);
              const bls__price = info_price.querySelector(".bls__price");
              bls__price.classList.remove("price--sold-out", "price--on-sale");
              bls__price
                .querySelector(".price__regular .price")
                .classList.remove("special-price");
              if (compare_price && compare_price > price) {
                const compare_format = Shopify.formatMoney(
                  compare_price,
                  cartStrings.money_format
                );
                if (bls__price.querySelector(".compare-price")) {
                  bls__price.querySelector(".compare-price").innerHTML =
                    `<span class="visually-hidden visually-hidden--inline">Regular price</span>${compare_format}`;
                }
                bls__price.classList.add("price--on-sale");
                bls__price
                  .querySelector(".price__regular .price")
                  .classList.add("special-price");
              }
              target.setAttribute("data-price", price);
              target.setAttribute("data-compare-price", compare_price);
            },
            false
          );
        });

      let totalQty = 0;
      const classQty = document.querySelectorAll(
        ".quantity__input-product-group"
      );
      classQty.forEach((input) => {
        let valueQtyDefault = input.value;
        let valueAsDefault = parseFloat(valueQtyDefault);
        if (!isNaN(valueAsDefault)) {
          totalQty += valueAsDefault;
        }
        input.addEventListener("change", () => {
          totalQty = 0;
          classQty.forEach((value) => {
            let valueQty = value.value;
            let valueAsQty = parseFloat(valueQty);
            if (!isNaN(valueAsQty)) {
              totalQty += valueAsQty;
            }
          });
        });
      });
      document.querySelectorAll(".product-group-submit").forEach((button) => {
        button.addEventListener(
          "click",
          (event) => {
            if (totalQty === 0) {
              const content = document.querySelector(
                ".form-infor .add-cart-error"
              );
              const messageErrQty = button.getAttribute(
                "data-add-cart-err-qty"
              );
              if (!content) return;
              var error_message = EasyDialogBox.create(
                "add_cart_error",
                "dlg dlg-disable-footer dlg-disable-drag dlg-disable-heading",
                "",
                (content.innerHTML = messageErrQty)
              );
              error_message.onClose = error_message.destroy;
              error_message.show();
            } else {
              this.submitProductGroup(event);
            }
          },
          false
        );
      });

      document.querySelectorAll(".product-group-buy-now").forEach((button) => {
        button.addEventListener(
          "click",
          (event) => {
            if (totalQty === 0) {
              const content = document.querySelector(
                ".form-infor .add-cart-error"
              );
              const messageErrQty = button.getAttribute(
                "data-add-cart-err-qty"
              );
              if (!content) return;
              var error_message = EasyDialogBox.create(
                "add_cart_error",
                "dlg dlg-disable-footer dlg-disable-drag dlg-disable-heading",
                "",
                (content.innerHTML = messageErrQty)
              );
              error_message.onClose = error_message.destroy;
              error_message.show();
            } else {
              this.submitNowProductGroup(event);
            }
          },
          false
        );
      });
    },

    submitProductGroup: function (event) {
      event.preventDefault();
      const target = event.currentTarget;
      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      const form = document.getElementById("form-product-grouped");
      const config = fetchConfig("json");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      let openMiniCart = 0;
      const formData = new FormData(form);
      if (cart) {
        formData.append(
          "sections",
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
      }
      config.body = formData;
      target.classList.add("btn-loading");
      fetch(`${routes.cart_add_url}.js`, config)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          fetch("/cart.json")
            .then((res) => res.json())
            .then((cart) => {
              if (cart.item_count != undefined) {
                window.updateCartCountBadges(cart.item_count);
                if (document.querySelector("header-total-price")) {
                  document
                    .querySelector("header-total-price")
                    .updateTotal(cart);
                }
                const cart_free_ship = document.querySelector(
                  "free-ship-progress-bar"
                );
                if (cart_free_ship) {
                  cart_free_ship.init(cart.items_subtotal_price);
                }
              }
            })
            .catch((error) => {
              throw error;
            });
          const parsedState = JSON.parse(state);
          if (parsedState.message) {
            const content = document.querySelector(
              ".form-infor .add-cart-error"
            );
            if (!content) return;
            var error_message = EasyDialogBox.create(
              "add_cart_error",
              "dlg dlg-disable-footer dlg-disable-drag dlg-disable-heading",
              "",
              (content.innerHTML = parsedState.description)
            );
            error_message.onClose = error_message.destroy;
            error_message.show();
          } else {
            parsedState.items.forEach((e) => {
              if (e.quantity > 0) {
                openMiniCart = 1;
              }
            });
            cart.getSectionsToRender().forEach((section) => {
              const elementToReplace = document.getElementById(section.id);
              const html = new DOMParser().parseFromString(
                parsedState.sections[section.id],
                "text/html"
              );
              elementToReplace.innerHTML =
                html.querySelector("#form-mini-cart").innerHTML;
              const countdown = cart.querySelector(".cart-countdown-time");
              const html_countdown = html.querySelector(".cart-countdown-time");
              if (countdown && html_countdown) {
                countdown.innerHTML = html_countdown.innerHTML;
                cart.countdownTimer();
              }
            });
            cart.cartAction();
          }
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {
          target.classList.remove("btn-loading");
          if (openMiniCart === 1) {
            cart.open();
          }
          BlsLazyloadImg.init();
        });
    },

    submitNowProductGroup: function (event) {
      event.preventDefault();
      const form = document.getElementById("form-product-grouped");
      const config = fetchConfig("json");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      const formData = new FormData(form);
      config.body = formData;
      fetch(`${routes.cart_add_url}.js`, config)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);
          if (parsedState.items) {
            window.location.href = "/checkout";
          } else {
            const content = document.querySelector(
              ".form-infor .add-cart-error"
            );
            if (!content) return;
            var error_message = EasyDialogBox.create(
              "add_cart_error",
              "dlg dlg-disable-footer dlg-disable-drag dlg-disable-heading",
              "",
              (content.innerHTML = parsedState.description)
            );
            error_message.onClose = error_message.destroy;
            error_message.show();
          }
        })
        .catch((e) => {
          throw e;
        });
    },

    showPopupStockNotify: function () {
      const stockClass = document.querySelectorAll(".product-notify-stock");
      const _this = this;
      stockClass.forEach((stock) => {
        stock.addEventListener("click", (e) => {
          const target = e.currentTarget;
          const variantId = target.getAttribute("data-product-variant");
          e.preventDefault();
          _this.fetchDataStockNotifySection(variantId);
        });
      });
    },

    fetchDataStockNotifySection: function (variantId) {
      const url = "/variants/" + variantId + "/?section_id=stock-notify";
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = newParser.parseFromString(responseText, "text/html");
          const id = html.querySelector("#bls-stock-notify");
          const text = id.getAttribute("data-stock-title");
          if (id) {
            var createPopupStock = EasyDialogBox.create(
              "stockNotify",
              "dlg dlg-disable-footer dlg-disable-drag",
              text,
              id.innerHTML
            );
            createPopupStock.onClose = createPopupStock.destroy;
            createPopupStock.show();
          }
        })
        .catch((e) => {
          throw e;
        });
    },

    actionDropdownSwatches: function () {
      document.querySelectorAll("[data-swatches-value]").forEach((items) => {
        items.addEventListener(
          "click",
          (e) => {
            const target = e.currentTarget;
            if (
              !target
                .closest(".bls__color-dropdown")
                .classList.contains("isClicked")
            ) {
              for (var item of document.querySelectorAll(
                ".bls__color-dropdown"
              )) {
                item.classList.remove("isClicked");
              }
              target.closest(".bls__color-dropdown").classList.add("isClicked");
            } else {
              target
                .closest(".bls__color-dropdown")
                .classList.remove("isClicked");
            }
          },
          false
        );
      });
      document
        .querySelectorAll(".bls__product-color-swatches-dropdown")
        .forEach((swatches) => {
          swatches.addEventListener("click", (e) => {
            const target = e.currentTarget;
            const valueSwatch = target.dataset.value;
            const container = target.closest(".bls__color-dropdown");
            container.querySelector(
              ".bls__color-dropdown-action .bls__color-dropdown-value"
            ).innerHTML = valueSwatch;
            target
              .closest(".bls__color-dropdown")
              .classList.remove("isClicked");
          });
        });
      document.body.addEventListener('click', this.handleBodyClick.bind(this), true); 
    },

    handleBodyClick: function(evt) {
      const target = evt.target;
      if (!target.closest(".bls__color-dropdown")) {
        for (var item of document.querySelectorAll(
          ".bls__color-dropdown"
        )) {
          item.classList.remove("isClicked");
        }
      }
    },

    eventSkeleton: function () {
      window.setTimeout(function () {
        this.document
          .getElementById("MainContent")
          .classList.remove("skeleton-product-detail");
      }, 1500);
    },
  };
})();
BlsEventMainProductShopify.init();

var BlsProductProp65 = (function () {
  const selector = '[data-product-block="prop65"]';
  const californiaRegion = "California";

  function getCookieValue(name) {
    if (typeof getCookie === "function") {
      return getCookie(name);
    }

    const cookies = document.cookie.split(";");
    const prefix = `${name}=`;
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf(prefix) === 0) {
        return decodeURIComponent(cookie.substring(prefix.length));
      }
    }

    return "";
  }

  function setCookieValue(name, value, days) {
    if (typeof setCookie === "function") {
      setCookie(name, value, days);
      return;
    }

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  }

  function renderNotice(block) {
    if (block.dataset.prop65Rendered === "true") return;

    block.innerHTML = `
      <div class="bls__product-prop65">
        <span class="bls__product-prop65__label">California Residents</span>
        <p class="bls__product-prop65__text">
          <strong>WARNING:</strong> This product can expose you to chemicals, including lead and arsenic, which are known to the State of California to cause birth defects or other reproductive harm. For more information go to
          <a
            class="bls__product-prop65__link"
            href="https://www.P65Warnings.ca.gov"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.P65Warnings.ca.gov
          </a>.
        </p>
      </div>
    `;
    block.dataset.prop65Rendered = "true";
  }

  function fetchRegion() {
    if (window.__blsProp65Region) {
      return Promise.resolve(window.__blsProp65Region);
    }

    if (window.__blsProp65RegionPromise) {
      return window.__blsProp65RegionPromise;
    }

    window.__blsProp65RegionPromise = fetch("https://ipinfo.io/json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Prop65 region lookup failed with ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const region = data?.region || "";
        if (region) {
          window.__blsProp65Region = region;
          setCookieValue("location_region", region, 1);
        }
        return region;
      })
      .catch(() => "")
      .finally(() => {
        window.__blsProp65RegionPromise = null;
      });

    return window.__blsProp65RegionPromise;
  }

  function initBlock(block) {
    if (block.dataset.prop65Initialized === "true") return;
    block.dataset.prop65Initialized = "true";

    const cachedRegion = getCookieValue("location_region");
    if (cachedRegion) {
      if (cachedRegion === californiaRegion) {
        renderNotice(block);
      }
      return;
    }

    fetchRegion().then((region) => {
      if (region === californiaRegion) {
        renderNotice(block);
      }
    });
  }

  function init(root = document) {
    root.querySelectorAll(selector).forEach((block) => {
      initBlock(block);
    });
  }

  return { init };
})();
BlsProductProp65.init();
document.addEventListener("shopify:section:load", function (event) {
  BlsProductProp65.init(event.target);
});

var BlsComplementaryProducts = (function () {
  const selector = ".bls__complementary-products";

  function init(root = document) {
    const blocks = root.querySelectorAll(selector);
    if (!blocks.length) return;
    blocks.forEach((block) => {
      if (block.dataset.complementaryInitialized === "true") return;
      block.dataset.complementaryInitialized = "true";
      fetchBlock(block);
    });
  }

  function fetchBlock(block) {
    const productId = block.dataset.productId;
    const limit = Number(block.dataset.limit || 4);
    const showSecondary = block.dataset.showSecondary === "true";
    const hideFallback = block.dataset.hideFallback === "true";
    const moneyFormat =
      block.dataset.moneyFormat || window.cartStrings?.money_format;
    const listEl = block.querySelector("[data-complementary-list]");
    const template = block.querySelector("template[data-complementary-template]");
    const loadingEl = block.querySelector(".bls__complementary-loading");
    if (!productId || !listEl) return;
    const intents =
      block.dataset.intent === "complementary_then_related"
        ? ["complementary", "related"]
        : [block.dataset.intent || "complementary"];
    const currentId = Number(productId);
    const rootUrl =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    if (hideFallback) {
      listEl.classList.add("complementary-fallback-hidden");
    }

    const fetchWithIntent = (intentIndex = 0) => {
      const intent = intents[intentIndex];
      if (!intent) {
        handleFallback();
        return;
      }
      const intentUrl = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      fetch(intentUrl)
        .then((response) => response.json())
        .then((data) => {
          const products = (data && data.products) || [];
          const filtered = currentId
            ? products.filter((item) => Number(item?.id) !== currentId)
            : products;
          if (filtered.length) {
            renderList(filtered.slice(0, limit));
          } else if (intents[intentIndex + 1]) {
            fetchWithIntent(intentIndex + 1);
          } else {
            handleFallback();
          }
        })
        .catch(() => {
          if (intents[intentIndex + 1]) {
            fetchWithIntent(intentIndex + 1);
          } else {
            handleFallback();
          }
        });
    };

    const renderList = (products) => {
      block.classList.remove("is-loading");
      loadingEl?.remove();
      listEl.classList.remove("complementary-fallback-hidden");
      (block.closest(".bls__complementary-wrapper") || block).classList.remove(
        "d-none"
      );
      listEl.innerHTML = "";
      products.forEach((product) => {
        const node = buildItem(product, {
          template,
          showSecondary,
          moneyFormat,
        });
        if (node) listEl.appendChild(node);
      });
      if (
        window.BlsSubActionProduct &&
        typeof BlsSubActionProduct.handleInitQuickviewAction === "function"
      ) {
        BlsSubActionProduct.handleInitQuickviewAction();
      }
    };

    const handleFallback = () => {
      block.classList.remove("is-loading");
      loadingEl?.remove();
      listEl.classList.remove("complementary-fallback-hidden");
      const hasItems = listEl.querySelector(".bls__complementary-item");
      if (!hasItems) {
        (block.closest(".bls__complementary-wrapper") || block).classList.add(
          "d-none"
        );
      }
    };

    fetchWithIntent();
  }

  function buildItem(product, options) {
    const template = options.template;
    const showSecondary = options.showSecondary;
    const moneyFormat = options.moneyFormat;
    if (!template) return null;
    const node = template.content.firstElementChild.cloneNode(true);

    const url = product?.url || "#";
    const handle = product?.handle;
    const title = product?.title || "";
    const price = normalizePrice(
      product?.price_min || product?.price || product?.variants?.[0]?.price
    );
    const compare = normalizePrice(
      product?.compare_at_price_max ||
        product?.compare_at_price ||
        product?.variants?.[0]?.compare_at_price
    );
    const primaryImage =
      getImage(
        product?.featured_image?.url || product?.featured_image,
        220
      ) || getImage(product?.images && product.images[0]?.src, 220);
    const secondaryImage =
      showSecondary && product?.images && product.images[1]
        ? getImage(product.images[1].src, 220)
        : null;

    if (handle && node.dataset) {
      node.dataset.productHandle = handle;
    }

    const titleEl = node.querySelector("[data-comp-title]");
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.setAttribute("href", url);
    }

    const buttonEl = node.querySelector("[data-comp-button]");
    if (buttonEl) {
      buttonEl.setAttribute("data-product-handle", handle || "");
    }

    const priceEl = node.querySelector("[data-comp-price]");
    if (priceEl) {
      priceEl.innerHTML = renderPrice(price, compare, moneyFormat);
    }

    const primaryImageEl = node.querySelector("[data-primary-image]");
    if (primaryImageEl && primaryImage) {
      primaryImageEl.setAttribute("src", primaryImage);
      primaryImageEl.setAttribute("alt", "");
    }
    const secondaryImageEl = node.querySelector("[data-secondary-image]");
    if (secondaryImageEl) {
      if (secondaryImage) {
        secondaryImageEl.setAttribute("src", secondaryImage);
        secondaryImageEl.classList.remove("d-none");
      } else {
        secondaryImageEl.remove();
      }
    }

    return node;
  }

  function normalizePrice(value) {
    if (value === null || value === undefined) return null;
    const numberValue = Number(value);
    if (!Number.isNaN(numberValue)) {
      return numberValue;
    }
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : Math.round(parsed * 100);
  }

  function renderPrice(price, compare, moneyFormat) {
    const formattedPrice = formatMoney(price, moneyFormat);
    if (!formattedPrice) return "";
    if (compare && compare > price) {
      const formattedCompare = formatMoney(compare, moneyFormat);
      return (
        '<span class="price price--highlight">' +
        formattedPrice +
        "</span>" +
        '<span class="price price--compare">' +
        formattedCompare +
        "</span>"
      );
    }
    return '<span class="price">' + formattedPrice + "</span>";
  }

  function formatMoney(price, moneyFormat) {
    if (price === null || price === undefined) return "";
    try {
      return Shopify.formatMoney(
        price,
        moneyFormat || window.cartStrings?.money_format
      );
    } catch (e) {
      return "";
    }
  }

  function getImage(url, size) {
    if (!url) return null;
    if (typeof url !== "string") {
      url = url.src || url.url;
    }
    if (!url) return null;
    try {
      const [path, query] = url.split("?");
      const cleanPath = path.replace(
        /_(\d+x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|master)\./,
        "."
      );
      const sizedPath = cleanPath.replace(
        /(\.[a-zA-Z0-9]+)$/,
        `_${size}x$1`
      );
      return query ? `${sizedPath}?${query}` : sizedPath;
    } catch (e) {
      return url;
    }
  }

  return { init };
})();
BlsComplementaryProducts.init();
document.addEventListener("shopify:section:load", function (event) {
  BlsComplementaryProducts.init(event.target);
});

var BlsEventProductSidebar = (function () {
  return {
    init: function () {
      this.initProductSidebar();
      this.productSidebarAction();
    },
    initProductSidebar: function () {
      const _this = this;
      const sidebar = document.querySelector(".bls__product-sidebar");
      const sectionId = sidebar?.getAttribute("data-section-id");
      const sidebarDrawer = document.querySelector(".bls-sidebar-content");
      const viewLayout = sidebar?.getAttribute("data-view");
      const domRender = document.querySelector(
        ".bls__template-product-sidebar"
      );
      const showSide = sidebar?.getAttribute("data-show-side");
      const urlInfo = window.location.href;
      const left = urlInfo.indexOf("view=left-sidebar") >= 1;
      const right = urlInfo.indexOf("view=right-sidebar") >= 1;
      const drawer = urlInfo.indexOf("view=drawer-sidebar") > -1;
      const url =
        "?section_id=" + sectionId + "&view=" + viewLayout + "-sidebar";
      if (sectionId !== undefined && viewLayout !== undefined) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            let newParser = new DOMParser();
            const html = newParser.parseFromString(responseText, "text/html");
            const id = html.querySelector(
              "#product-sidebar-content"
            )?.innerHTML;
            if (left || right || drawer) {
              if (left || right) {
                domRender.innerHTML = id;
              } else if (drawer) {
                sidebarDrawer.innerHTML = id;
              }
              sidebarDrawer.innerHTML = id;
            } else {
              if (showSide == "true") {
                if (viewLayout == "left" || viewLayout == "right") {
                  domRender.innerHTML = id;
                } else if (viewLayout == "drawer") {
                  document.querySelector(
                    ".bls__template-main-product"
                  ).style.width = "100%";
                  document
                    .querySelector(".bls-sidebar-drawer")
                    .removeAttribute("style");
                  sidebarDrawer.innerHTML = id;
                }
                sidebarDrawer.innerHTML = id;
              }
            }
            if (Shopify.designMode) {
              if (showSide == "true") {
                if (viewLayout == "left" || viewLayout == "right") {
                  domRender.innerHTML = id;
                  document.querySelector(
                    ".bls__template-product-sidebar"
                  ).style.display = "block";
                  document.querySelector(
                    ".bls__template-main-product"
                  ).style.width = "75%";
                } else if (viewLayout == "drawer") {
                  sidebarDrawer.innerHTML = id;
                  document
                    .querySelector(".bls-sidebar-drawer")
                    .removeAttribute("style");
                  document.querySelector(
                    ".bls__template-main-product"
                  ).style.width = "100%";
                  document.querySelector(
                    ".bls__template-product-sidebar"
                  ).style.display = "none";
                }
                sidebarDrawer.innerHTML = id;
              } else {
                if (viewLayout == "left" || viewLayout == "right") {
                  document.querySelector(
                    ".bls__template-product-sidebar"
                  ).style.display = "none";
                }
                document.querySelector(
                  ".bls__template-main-product"
                ).style.width = "100%";
              }
            }
          })
          .catch((e) => {
            throw e;
          })
          .finally(() => {
            const removeSidebar = document.querySelector(".contain-sidebar");
            if (removeSidebar) {
              removeSidebar.remove();
            }
            BlsInstagramShopify.init();
            _this.initToggleSidebar();
            _this.productSidebarCheckLayout();
          });
      }
    },

    open: function () {
      const drawer = document.querySelector(".bls-sidebar-drawer");
      drawer.classList.add("bls__opend-popup-header");
      document
        .querySelector(".bls__overlay")
        .classList.remove("d-none-overlay");
      drawer.addEventListener(
        "transitionend",
        () => {
          drawer.focus();
        },
        { once: true }
      );
      document.body.addEventListener("click", this.onBodyClick);
    },

    close: function () {
      const drawer = document.querySelector(".bls-sidebar-drawer");
      drawer.classList.remove("bls__opend-popup-header");
      document.querySelector(".bls__overlay").classList.add("d-none-overlay");
      document.documentElement.classList.remove("hside_opened");
      document.body.removeEventListener("click", this.onBodyClick);
    },

    productSidebarAction: function () {
      const action = document.querySelector(".action-product-sidebar");
      const drawer = document.querySelector(".bls-sidebar-drawer");
      action?.addEventListener("click", () => {
        if (drawer.classList.contains("bls__opend-popup-header")) {
          this.close();
        } else {
          this.open();
        }
      });
      document
        .querySelector(".bls-sidebar-drawer-wrapper .close-button")
        ?.addEventListener("click", () => {
          this.close();
        });
      document.querySelector(".bls__overlay").addEventListener("click", () => {
        this.close();
      });
    },

    initToggleSidebar: function () {
      var item_parent = ".sidebar-item > .sidebar-title";
      document.querySelectorAll(item_parent).forEach((item) => {
        item.addEventListener("click", (e) => {
          e.preventDefault();
          const target = e.currentTarget;
          const parent = target.parentElement;
          const sideContent = parent.querySelector(".sidebar-content");
          slideAnime({
            target: sideContent,
            animeType: "slideToggle",
          });
          if (item.closest(".sidebar-item").classList.contains("active")) {
            item.closest(".sidebar-item").classList.remove("active");
          } else {
            item.closest(".sidebar-item").classList.add("active");
          }
        });
      });
    },

    productSidebarCheckLayout: function () {
      const urlInfo = window.location.href;
      const left = urlInfo.indexOf("view=left-sidebar") > -1;
      const right = urlInfo.indexOf("view=right-sidebar") > -1;
      const drawer = urlInfo.indexOf("view=drawer-sidebar") > -1;
      if (left) {
        document.querySelector(".bls__template-product-sidebar").style.order =
          "-1";
      } else if (right) {
        document.querySelector(".bls__template-product-sidebar").style.order =
          "1";
      } else if (drawer) {
        document.querySelector(".bls-sidebar-drawer").removeAttribute("style");
        document.querySelector(".bls__template-main-product").style.width =
          "100%";
      }
    },
  };
})();
BlsEventProductSidebar.init();
document.addEventListener("shopify:section:load", function (event) {
  var section = event.target.querySelector(".bls__product-sidebar");
  if (section != undefined) {
    BlsEventProductSidebar.init();
  }
});

var BlsZoomThumbsnail = (function () {
  return {
    init: function () {
      this.initZoomImage();
    },

    initZoomImage: function () {
      const mainImg = document.querySelectorAll(".bls__zoom-hover img");
      if (mainImg) {
        const zoomOption = document.querySelector("[data-zoom-option]")?.getAttribute("data-zoom-option");
        mainImg.forEach((trigger) => {
          const paneContainer = trigger
            .closest(".bls__template-main-product")
            .querySelector(".bls__zoom-external");
          const inlineContainer = trigger.closest("[data-pane-container]");
          new Drift(trigger, {
            sourceAttribute: "data-zoom",
            inlinePane: zoomOption === "inner-2",
            zoomFactor: 2,
            containInline: paneContainer,
            paneContainer:
              zoomOption === "external" ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === "external",
            onShow: function () {
              inlineContainer.classList.add("relative");
            },
            onHide: function () {
              inlineContainer.classList.remove("relative");
            },
          });
        });
      }
    },
  };
})();
BlsZoomThumbsnail.init();

class ScrollReview extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.onButtonClick.bind(this));
  }
  onButtonClick(e) {
    e.preventDefault();
    let target = document.querySelector(".bls__product-tabs");
    if (target.querySelectorAll(".tab-content-all").length) {
      target = document.querySelector("[tab-review]") || document.querySelector("[tab-review-app]");
    }
    if (target.classList.contains("inside-product-main-infomation")) {
      target = document.querySelector(".bls__product-tabs-content");
    }
    if (!target) return;
    var scrollContainer = target;
    do {
      scrollContainer = scrollContainer.parentNode;
      if (!scrollContainer) return;
      scrollContainer.scrollTop += 1;
    } while (scrollContainer.scrollTop == 0);

    var targetY = 0;
    do {
      if (target == scrollContainer) break;
      targetY += target.offsetTop;
    } while ((target = target.offsetParent));

    scroll = function (c, a, b, i) {
      i++;
      if (i > 30) return;
      c.scrollTop = a + ((b - a) / 30) * i;
      setTimeout(function () {
        scroll(c, a, b, i);
      }, 10);
    };
    scroll(scrollContainer, scrollContainer.scrollTop, targetY, 0);
    setTimeout(() => {
      this.eventProductTabs();
    }, 300);
  }
  eventProductTabs() {
    const target = document.querySelector("[tab-review]") || document.querySelector("[tab-review-app]");
    if (!target) return;
    if (target.classList.contains("accordition")) {
      const tab_id = target.getAttribute("data-block-id");
      const conditions = document.getElementById(tab_id);
      if (!conditions) return;
      if (!conditions.classList.contains("active")) {
        for (var item of document.querySelectorAll(options.tabContent)) {
          item.classList.remove("active");
          slideAnime({
            target: item.querySelector(".tab-panel"),
            animeType: "slideUp",
          });
        }
        slideAnime({
          target: conditions.querySelector(".tab-panel"),
          animeType: "slideDown",
        });
        conditions.classList.add("active");
      }
    }
    if (target.classList.contains("is-nav-tabs")) {
      const tab_id = target.getAttribute("data-block-id");
      if (!target.closest(".data.item").classList.contains("active")) {
        for (var item of document.querySelectorAll(".data.item")) {
          item.classList.remove("active");
        }
        for (var item of document.querySelectorAll(options.tabContent)) {
          item.classList.remove("active");
          item.querySelector(".tab-panel").style.display = "none";
        }
        const conditions = document.getElementById(tab_id);
        conditions.classList.add("active");
        conditions.querySelector(".tab-panel").style.display = "block";
        target.closest(".data.item").classList.add("active");
      }
    }
  }
}
customElements.define("review-scroll", ScrollReview);

const initPdpInfoHoverScroll = () => {
  const mediaQuery = window.matchMedia("(min-width: 992px)");
  if (!mediaQuery.matches) return;
  document
    .querySelectorAll(".bls__template-main-product.sticky-product-information")
    .forEach((section) => {
      const info = section.querySelector(".bls__product-details-infor");
      if (!info) return;
      const images = section.querySelector(".bls__product-images");
      let infoScrollActive = false;

      const enableInfoScroll = () => {
        if (infoScrollActive) return;
        const parent = info.offsetParent || info.parentElement;
        if (!parent) return;
        const infoRect = info.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        const normalTop = parentRect.top + info.offsetTop;
        const delta = infoRect.top - normalTop;
        info.style.setProperty("--pdp-info-offset", `${delta}px`);
        section.classList.add("is-info-hover");
        infoScrollActive = true;
      };

      const disableInfoScroll = () => {
        if (!infoScrollActive) return;
        section.classList.remove("is-info-hover");
        info.style.removeProperty("--pdp-info-offset");
        infoScrollActive = false;
      };

      info.addEventListener("wheel", enableInfoScroll, { passive: true });
      if (images) {
        images.addEventListener("wheel", disableInfoScroll, { passive: true });
      }
    });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPdpInfoHoverScroll);
} else {
  initPdpInfoHoverScroll();
}

// Advanced subscription selector (Skio).
const ADVANCED_SUBSCRIPTION_SELECTOR = "[data-advanced-subscription]";

const formatAdvancedSubscriptionMoney = (amount, moneyFormat) => {
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(amount, moneyFormat);
  }
  return amount;
};

const initAdvancedSubscription = (container) => {
  if (!container || container.dataset.advancedSubscriptionInitialized === "true") {
    return;
  }
  container.dataset.advancedSubscriptionInitialized = "true";

  const settingsScript = container.querySelector("[data-advanced-subscription-settings]");
  const productScript = container.querySelector("[data-advanced-subscription-product]");
  const bundleProductsScript = container.querySelector("[data-advanced-subscription-bundle-products]");
  if (!settingsScript || !productScript) {
    return;
  }

  let settings;
  let product;
  let bundleProducts = [];
  try {
    settings = JSON.parse(settingsScript.textContent);
    product = JSON.parse(productScript.textContent);
    bundleProducts = bundleProductsScript ? JSON.parse(bundleProductsScript.textContent) : [];
  } catch (error) {
    return;
  }

  const formId = container.dataset.productForm;
  const form = formId ? document.getElementById(formId) : null;
  if (!form || !product) {
    return;
  }
  const sectionId = container.dataset.sectionId;
  const stickyForm = sectionId ? document.getElementById(`sticky-addcart-form-${sectionId}`) : null;
  const stickySellingPlanInput = stickyForm?.querySelector("[data-sticky-selling-plan]");
  const stickyVariantInput = stickyForm?.querySelector('[name="id"]');
  const stickyQuantityInput = stickyForm?.querySelector('input[name="quantity"]');

  const planInput = container.querySelector("[data-advanced-subscription-input]");
  const subscriptionRadio = container.querySelector(
    '[data-advanced-subscription-radio="subscription"]'
  );
  const oneTimeRadio = container.querySelector(
    '[data-advanced-subscription-radio="one-time"]'
  );
  const planSelect = container.querySelector("[data-advanced-subscription-plan-select]");
  const oneTimeDiscountBadge = container.querySelector(
    "[data-advanced-subscription-one-time-discount]"
  );
  const subscriptionPrice = container.querySelector("[data-advanced-subscription-price]");
  const subscriptionCompare = container.querySelector("[data-advanced-subscription-compare]");
  const oneTimeCompare = container.querySelector("[data-advanced-subscription-one-time-compare]");
  const oneTimePrice = container.querySelector("[data-advanced-subscription-one-time-price]");
  const subscriptionPriceValue = container.querySelector(
    "[data-advanced-subscription-price-value]"
  );
  const subscriptionCompareValue = container.querySelector(
    "[data-advanced-subscription-compare-value]"
  );
  const oneTimePriceValue = container.querySelector(
    "[data-advanced-subscription-one-time-price-value]"
  );
  const oneTimeCompareValue = container.querySelector(
    "[data-advanced-subscription-one-time-compare-value]"
  );
  const oneTimeVolumeBadges = container.querySelector(
    "[data-advanced-subscription-one-time-badges]"
  );
  const oneTimeVolumeTiles = container.querySelector(
    "[data-advanced-subscription-one-time-tiles]"
  );
  const subscriptionOption = container.querySelector(
    '[data-advanced-subscription-option="subscription"]'
  );
  const oneTimeOption = container.querySelector(
    '[data-advanced-subscription-option="one-time"]'
  );
  const moneyFormat = window.cartStrings?.money_format;
  const sellingPlanGroups = (product.selling_plan_groups || []).filter((group) => {
    const appId = (group.app_id || "").toString().toLowerCase();
    const groupName = (group.name || "").toString().toLowerCase();
    return appId === "skio" && groupName === "dynamic box subscription";
  });

  const sellingPlanIds = new Set();
  const sellingPlanMap = {};
  sellingPlanGroups.forEach((group) => {
    (group.selling_plans || []).forEach((plan) => {
      sellingPlanIds.add(plan.id);
      sellingPlanMap[plan.id] = plan;
    });
  });

  let currentVariant = null;
  let currentAllocations = [];
  let selectedPlanId = null;
  let initialized = false;
  let activeBundleSlot = null;
  const bundleSlots = [null, null, null];
  let firstSlotWasRemoved = false;
  let bundleModal = null;

  const getVariantById = (variantId) =>
    (product.variants || []).find((variant) => variant.id === variantId);

  const getVisualDiscountPricing = (variant) => {
    const discountSource =
      container.closest(".bls__template-product") ||
      container.closest(".bls__product-single") ||
      container;
    const enabled = discountSource.dataset.globalVisualDiscountEnabled === "true";
    const percent = Number.parseFloat(discountSource.dataset.globalVisualDiscountPercent || "0");
    let compareAtPrice = variant?.compare_at_price || null;
    let price = variant?.price || 0;
    let hasVisualDiscount = false;

    if (enabled && percent > 0) {
      const originalPrice = compareAtPrice && compareAtPrice > price ? compareAtPrice : price;
      compareAtPrice = originalPrice;
      price = Math.round(originalPrice * ((100 - percent) / 100));
      hasVisualDiscount = price < originalPrice;
    }

    return {
      compareAtPrice,
      price,
      hasSale: hasVisualDiscount || Boolean(compareAtPrice && compareAtPrice > price),
    };
  };

  const hasConflictingSale = () => {
    const pricing = getVisualDiscountPricing(currentVariant);
    return pricing.hasSale;
  };

  const isOneTimeVolumeDiscountEnabled = () =>
    container.dataset.onetimeVolumeDiscountEnabled !== "false" &&
    !hasConflictingSale() &&
    (
      window.oneTimeVolumeDiscountEnabled === undefined ||
      window.oneTimeVolumeDiscountEnabled !== false
    );

  const parseDiscountSteps = (rawSteps, fallback) => {
    const steps = rawSteps
      .split(",")
      .map((step) => {
        const match = step.match(/(\d+(?:\.\d+)?)/);
        return match ? Number(match[1]) : 0;
      });

    return steps.length ? steps : fallback;
  };

  const getOneTimeVolumeDiscountSteps = () =>
    parseDiscountSteps(container.dataset.onetimeVolumeDiscountSteps || "0%,5%,10%", [0, 5, 10]);

  const getSubscriptionVolumeDiscountSteps = () =>
    parseDiscountSteps(container.dataset.subscriptionVolumeDiscountSteps || "10%,15%,20%", [10, 15, 20]);

  const syncOneTimeVolumeBadges = () => {
    if (!oneTimeVolumeBadges) {
      return;
    }

    const steps = getOneTimeVolumeDiscountSteps();
    const labels = steps
      .map((discount, index) => {
        if (!discount || discount <= 0) {
          return "";
        }
        const quantityLabel = index === steps.length - 1 ? `${index + 1}+ ITEMS` : `${index + 1} ITEMS`;
        return `BUNDLE ${quantityLabel}, SAVE ${discount}%`;
      })
      .filter(Boolean);

    oneTimeVolumeBadges.hidden = !isOneTimeVolumeDiscountEnabled() || !labels.length;
    oneTimeVolumeBadges.replaceChildren(
      ...labels.map((label) => {
        const badge = document.createElement("span");
        badge.textContent = label;
        return badge;
      })
    );
  };

  const syncOneTimeVolumeTiles = () => {
    if (!oneTimeVolumeTiles) {
      return;
    }

    const steps = getOneTimeVolumeDiscountSteps();
    const isEnabled = isOneTimeVolumeDiscountEnabled();
    oneTimeVolumeTiles.hidden = !isEnabled;
    oneTimeVolumeTiles.replaceChildren();

    if (!isEnabled) {
      return;
    }

    const slotDiscounts = [steps[0] || 0, steps[1] || 0, steps[2] || 0];
    slotDiscounts.forEach((discount, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "advanced-subscription__bundle-tile";
      button.dataset.advancedSubscriptionSlot = String(index);
      button.setAttribute("aria-label", index === 0 ? "Bundle item 1" : `Add bundle item ${index + 1}`);

      const badge = document.createElement("div");
      badge.className = "advanced-subscription__bundle-tile-badge";
      badge.textContent = getOneTimeTileBadgeLabel(index);

      const body = document.createElement("div");
      body.className = "advanced-subscription__bundle-tile-body";
      body.dataset.advancedSubscriptionSlotBody = String(index);
      body.dataset.advancedSubscriptionSlotBodyOneTime = String(index);

      button.append(badge, body);
      oneTimeVolumeTiles.appendChild(button);
    });

    renderBundleSlots();
  };

  const getProductFormElement = () => form.closest("product-form");
  const getProductFormQuantity = () => {
    const rawValue = form.querySelector('input[name="quantity"]')?.value;
    const parsedValue = Number.parseInt(rawValue || "1", 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1;
  };

  const syncStickyFormState = () => {
    if (!stickyForm) {
      return;
    }

    if (stickyVariantInput && currentVariant?.id) {
      stickyVariantInput.value = String(currentVariant.id);
    }

    if (stickyQuantityInput) {
      stickyQuantityInput.value = String(getProductFormQuantity());
    }

    if (stickySellingPlanInput) {
      stickySellingPlanInput.value = planInput?.value || "";
    }
  };

  const submitMainProductFormFromSticky = (event) => {
    if (!stickyForm) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const mainQuantityInput = form.querySelector('input[name="quantity"]');
    if (mainQuantityInput && stickyQuantityInput) {
      mainQuantityInput.value = stickyQuantityInput.value || "1";
      mainQuantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      mainQuantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    const mainVariantInput = form.querySelector('input[name="id"]');
    if (mainVariantInput && stickyVariantInput) {
      mainVariantInput.value = stickyVariantInput.value;
      mainVariantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    syncStickyFormState();
    form.requestSubmit();
  };

  const getSelectedBundleItems = () =>
    bundleSlots.flatMap((item, index) => {
      if (!item) {
        return [];
      }

      const quantity = index === 0 ? getProductFormQuantity() : 1;
      return Array.from({ length: quantity }, () => item);
    });

  const getBundleItemCount = () => Math.max(1, getSelectedBundleItems().length);

  const getCurrentProductOption = () => {
    const fallbackProduct = bundleProducts.find((item) => item.id === product.id) || bundleProducts[0] || {};
    return {
      ...fallbackProduct,
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: fallbackProduct.image || product.featured_image,
      variantId: currentVariant?.id || fallbackProduct.variantId,
      variantTitle: currentVariant?.title || fallbackProduct.variantTitle,
      price: currentVariant?.price || fallbackProduct.price || 0,
      compareAtPrice: currentVariant?.compare_at_price || fallbackProduct.compareAtPrice || null,
      available: currentVariant?.available !== false,
      sellingPlanAllocations: currentVariant?.selling_plan_allocations || fallbackProduct.sellingPlanAllocations || [],
    };
  };

  const getItemBasePrice = (item) => {
    const compareAtPrice = Number(item?.compareAtPrice || 0);
    const price = Number(item?.price || 0);
    return compareAtPrice > price ? compareAtPrice : price;
  };

  const getBundleBaseTotal = () =>
    getSelectedBundleItems().reduce((total, item) => total + getItemBasePrice(item), 0);

  const getBundleSellingTotal = () =>
    getSelectedBundleItems().reduce((total, item) => total + Number(item?.price || 0), 0);

  const getTierDiscountPercent = (steps) => {
    const quantity = getBundleItemCount();
    const discount = steps[Math.min(Math.max(quantity - 1, 0), steps.length - 1)] || 0;
    return Number.isFinite(discount) ? discount : 0;
  };

  const getOneTimeDiscountPercent = () => {
    if (!isOneTimeVolumeDiscountEnabled()) {
      return 0;
    }

    return getTierDiscountPercent(getOneTimeVolumeDiscountSteps());
  };

  const getOneTimeTileBadgeLabel = (slotIndex, quantity = 1) => {
    const steps = getOneTimeVolumeDiscountSteps();
    const normalizedSlotIndex = Math.max(Number(slotIndex) || 0, 0);
    const normalizedQuantity = Math.max(Number(quantity) || 1, 1);
    const effectiveIndex = Math.min(
      normalizedSlotIndex + normalizedQuantity - 1,
      Math.max(steps.length - 1, 0)
    );
    const discount = steps[effectiveIndex] || 0;

    return discount > 0 ? `Save ${discount}%` : "Base price";
  };

  const getSubscriptionTileBadgeLabel = (slotIndex, quantity = 1) => {
    const steps = getSubscriptionVolumeDiscountSteps();
    const normalizedSlotIndex = Math.max(Number(slotIndex) || 0, 0);
    const normalizedQuantity = Math.max(Number(quantity) || 1, 1);
    const effectiveIndex = Math.min(
      normalizedSlotIndex + normalizedQuantity - 1,
      Math.max(steps.length - 1, 0)
    );
    const discount = steps[effectiveIndex] || 0;

    return discount > 0 ? `Save ${discount}%` : "Base price";
  };

  const getSubscriptionDiscountPercent = () =>
    getTierDiscountPercent(getSubscriptionVolumeDiscountSteps());

  // Match the selected Skio plan to the normal subscription volume tier.
  const getSubscriptionRenewalDiscountPercent = () =>
    getTierDiscountPercent([10, 15, 20]);

  const setNodeHtml = (node, value) => {
    if (!node) {
      return;
    }
    node.innerHTML = value;
  };

  const formatBundleMoney = (cents) => formatAdvancedSubscriptionMoney(cents, moneyFormat);

  const getBundleSlotKey = (item, index) => {
    if (item?.variantId) {
      return `variant:${item.variantId}`;
    }

    if (item?.id) {
      return `product:${item.id}`;
    }

    return `slot:${index}`;
  };

  const getBundleSlotDisplayMeta = (slotCount = 3, getBadgeLabel = getOneTimeTileBadgeLabel) => {
    const groups = new Map();
    const orderedGroups = [];

    bundleSlots.forEach((item, index) => {
      if (!item) {
        return;
      }

      const key = getBundleSlotKey(item, index);
      const existingGroup = groups.get(key);

      if (existingGroup) {
        existingGroup.indexes.push(index);
        existingGroup.quantity += 1;
        return;
      }

      const group = {
        item,
        indexes: [index],
        quantity: index === 0 ? getProductFormQuantity() : 1,
      };

      groups.set(key, group);
      orderedGroups.push(group);
    });

    const displayMeta = Array.from({ length: slotCount }, () => ({
      hidden: false,
      item: null,
      quantity: 0,
      indexes: [],
      badgeLabel: "",
    }));

    let cursor = 0;
    orderedGroups.forEach((group) => {
      if (cursor >= slotCount) {
        return;
      }

      displayMeta[cursor] = {
        hidden: false,
        item: group.item,
        quantity: group.quantity,
        indexes: group.indexes,
        badgeLabel: getBadgeLabel(cursor, group.quantity),
      };

      const consumedSlots = Math.min(Math.max(group.quantity, 1), slotCount - cursor);
      for (let offset = 1; offset < consumedSlots; offset += 1) {
        displayMeta[cursor + offset] = {
          hidden: true,
          item: null,
          quantity: 0,
          indexes: group.indexes,
          badgeLabel: "",
        };
      }

      cursor += consumedSlots;
    });

    return displayMeta;
  };

  const renderSlotProduct = (body, item, options = {}) => {
    const quantity = Number(options.quantity || 1);
    const indexes = Array.isArray(options.indexes) ? options.indexes : [];

    body.replaceChildren();
    body.removeAttribute("aria-hidden");

    if (!item) {
      body.setAttribute("aria-hidden", "true");
      body.textContent = "+";
      return;
    }

    const wrapper = document.createElement("span");
    wrapper.className = "advanced-subscription__slot-product";

    const removeButton = document.createElement("span");
    removeButton.className = "advanced-subscription__slot-remove";
    removeButton.dataset.advancedSubscriptionSlotRemove = "true";
    if (indexes.length) {
      removeButton.dataset.advancedSubscriptionSlotRemoveIndexes = indexes.join(",");
    }
    removeButton.dataset.advancedSubscriptionSlotRemoveQuantity = String(quantity);
    removeButton.setAttribute("aria-label", `Remove ${item.title || "selected product"}`);
    removeButton.setAttribute("role", "button");
    removeButton.tabIndex = 0;
    removeButton.setAttribute("aria-hidden", "false");
    wrapper.appendChild(removeButton);

    if (quantity > 1) {
      const quantityBadge = document.createElement("span");
      quantityBadge.className = "advanced-subscription__slot-quantity";
      quantityBadge.textContent = String(quantity);
      quantityBadge.setAttribute("aria-label", `${quantity} items selected`);
      wrapper.appendChild(quantityBadge);
    }

    if (item.image) {
      const image = document.createElement("img");
      image.src = item.image;
      image.alt = item.title || "";
      image.loading = "lazy";
      wrapper.appendChild(image);
    }

    const title = document.createElement("span");
    title.className = "advanced-subscription__slot-title";
    title.textContent = item.title || "Selected product";
    wrapper.appendChild(title);

    body.appendChild(wrapper);
  };

  const renderSlotBodies = (slotBodies, getBadgeLabel) => {
    const displayMeta = getBundleSlotDisplayMeta(slotBodies.length || 3, getBadgeLabel);

    slotBodies.forEach((body) => {
      const index = Number.parseInt(body.dataset.advancedSubscriptionSlotBody || "0", 10);
      const slotMeta = displayMeta[index] || {
        hidden: false,
        item: null,
        quantity: 0,
        indexes: [],
        badgeLabel: "",
      };
      const tile = body.closest("[data-advanced-subscription-slot]");

      if (tile) {
        tile.hidden = slotMeta.hidden;
        tile.classList.toggle("is-selected", Boolean(slotMeta.item));
        const badge = tile.querySelector(".advanced-subscription__bundle-tile-badge");
        if (badge) {
          badge.textContent = slotMeta.badgeLabel || getBadgeLabel(index);
        }
      }

      if (slotMeta.hidden) {
        body.replaceChildren();
        return;
      }

      renderSlotProduct(body, slotMeta.item, {
        quantity: slotMeta.quantity,
        indexes: slotMeta.indexes,
      });
    });
  };

  function renderBundleSlots() {
    if (!bundleSlots[0] && currentVariant && !firstSlotWasRemoved) {
      bundleSlots[0] = getCurrentProductOption();
    } else if (bundleSlots[0]?.id === product.id && currentVariant) {
      bundleSlots[0] = getCurrentProductOption();
    }

    renderSlotBodies(
      Array.from(container.querySelectorAll("[data-advanced-subscription-slot-body-subscription]")),
      getSubscriptionTileBadgeLabel
    );
    renderSlotBodies(
      Array.from(container.querySelectorAll("[data-advanced-subscription-slot-body-one-time]")),
      getOneTimeTileBadgeLabel
    );
  }

  const getSellingPlanForItem = (item) => {
    if (!item || !selectedPlanId) {
      return null;
    }

    const allocations = item.sellingPlanAllocations || [];
    const exact = allocations.find((allocation) => Number(allocation.selling_plan_id) === selectedPlanId);
    if (exact?.selling_plan_id) {
      return exact.selling_plan_id;
    }

    const sellingPlanIds = (item.sellingPlanIds || [])
      .map((planId) => Number(planId))
      .filter((planId) => Number.isFinite(planId));

    if (sellingPlanIds.includes(Number(selectedPlanId))) {
      return selectedPlanId;
    }

    const selectedPlan = sellingPlanMap[selectedPlanId];
    const matchingAllocation = allocations.find((allocation) => {
      const plan = sellingPlanMap[allocation.selling_plan_id];
      return (
        getPlanRenewalDiscountPercent(plan) === getPlanRenewalDiscountPercent(selectedPlan) &&
        getPlanDeliveryKey(plan) === getPlanDeliveryKey(selectedPlan)
      );
    });

    if (matchingAllocation?.selling_plan_id) {
      return matchingAllocation.selling_plan_id;
    }

    // Some Shopify product-list contexts omit variant allocation objects even
    // when the product belongs to the same Skio selling plan group. Let the
    // cart API validate the selected plan instead of blocking valid bundles.
    return selectedPlanId;
  };

  const buildBundleCartItems = () => {
    const isSubscription = subscriptionRadio ? subscriptionRadio.checked : true;
    if (isSubscription) {
      syncSelectedPlanToBundleTier();
    }
    const rawItems = getSelectedBundleItems()
      .filter((item) => item?.variantId)
      .map((item) => {
        const line = {
          id: Number(item.variantId),
          quantity: 1,
        };
        const sellingPlanId = isSubscription ? getSellingPlanForItem(item) : null;
        if (sellingPlanId) {
          line.selling_plan = Number(sellingPlanId);
        } else if (isSubscription) {
          line.missing_selling_plan = true;
        }
        return line;
      });

    const mergedItems = new Map();

    rawItems.forEach((line) => {
      const key = `${line.id}:${line.selling_plan || "none"}:${line.missing_selling_plan ? "missing" : "ok"}`;
      const existing = mergedItems.get(key);

      if (existing) {
        existing.quantity += Number(line.quantity || 1);
        return;
      }

      mergedItems.set(key, { ...line });
    });

    return [...mergedItems.values()];
  };

  const refreshDynamicCartImages = (root = document) => {
    if (window.BlsLazyloadImg && typeof window.BlsLazyloadImg.init === "function") {
      window.BlsLazyloadImg.init();
    }

    root.querySelectorAll("img[data-src]").forEach((image) => {
      if (!image.getAttribute("src") || image.getAttribute("src") === "data:,") {
        image.setAttribute("src", image.dataset.src);
      }
    });
  };

  const updateCartAfterBundleAdd = (parsedState, { openDrawer = true } = {}) => {
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    if (parsedState?.item_count != null) {
      document.querySelectorAll(".cart-count").forEach((el) => {
        el.innerHTML = el.classList.contains("cart-count-drawer")
          ? `(${parsedState.item_count})`
          : parsedState.item_count;
      });
    }

    if (cart && parsedState?.sections && typeof cart.getSectionsToRender === "function") {
      cart.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id);
        const sectionHtml = parsedState.sections[section.id];
        if (!elementToReplace || !sectionHtml) {
          return;
        }
        const html = new DOMParser().parseFromString(sectionHtml, "text/html");
        const replacement = html.querySelector("#form-mini-cart") || html.querySelector(section.selector);
        if (replacement) {
          elementToReplace.innerHTML = replacement.innerHTML;
        }
      });
      refreshDynamicCartImages(cart);

      if (openDrawer && typeof cart.cartAction === "function") {
        cart.cartAction();
      }
      if (openDrawer && typeof cart.open === "function") {
        cart.open();
      }
      window.setTimeout(() => refreshDynamicCartImages(cart), 50);
    }

    fetch("/cart.json")
      .then((response) => response.json())
      .then((cartState) => {
        if (cartState.item_count != null) {
          document.querySelectorAll(".cart-count").forEach((el) => {
            el.innerHTML = el.classList.contains("cart-count-drawer")
              ? `(${cartState.item_count})`
              : cartState.item_count;
          });
        }
        const freeShip = document.querySelector("free-ship-progress-bar");
        if (freeShip && typeof freeShip.init === "function") {
          freeShip.init(cartState.items_subtotal_price);
        }
      })
      .catch(() => {});
  };

  const submitBundleCart = async (event) => {
    const isSubscription = subscriptionRadio ? subscriptionRadio.checked : true;
    if (!isSubscription) {
      return;
    }
    const items = buildBundleCartItems();
    const firstItemDiffersFromCurrent = items[0]?.id && Number(items[0].id) !== Number(currentVariant?.id);
    if (isSubscription && items.length <= 1 && !firstItemDiffersFromCurrent) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (items.some((item) => item.missing_selling_plan)) {
      getProductFormElement()?.handleErrorMessage?.("One or more selected products are not available for subscription.");
      return;
    }

    const productFormElement = getProductFormElement();
    const submitButton = productFormElement?.querySelector('[type="submit"]');
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    if (submitButton) {
      submitButton.setAttribute("disabled", "true");
      submitButton.classList.add("btn-loading");
    }

    const payload = {
      items,
      sections_url: window.location.pathname,
    };

    if (cart && typeof cart.getSectionsToRender === "function") {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      if (typeof cart.setActiveElement === "function") {
        cart.setActiveElement(document.activeElement);
      }
    }

    try {
      const response = await fetch(routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });
      const parsedState = await response.json();

      if (!response.ok || parsedState.errors) {
        throw new Error(parsedState.description || parsedState.message || "Unable to add bundle");
      }

      updateCartAfterBundleAdd(parsedState, { openDrawer: isSubscription });
    } catch (error) {
      productFormElement?.handleErrorMessage?.(error.message);
    } finally {
      if (submitButton) {
        submitButton.classList.remove("btn-loading");
        submitButton.removeAttribute("disabled");
        submitButton.querySelector("span")?.classList.remove("hidden");
      }
    }
  };

  const createBundleModal = () => {
    if (bundleModal) {
      return bundleModal;
    }

    bundleModal = document.createElement("div");
    bundleModal.className = "advanced-subscription-modal";
    bundleModal.hidden = true;
    bundleModal.innerHTML = [
      '<div class="advanced-subscription-modal__backdrop" data-advanced-subscription-modal-close></div>',
      '<div class="advanced-subscription-modal__panel" role="dialog" aria-modal="true" aria-label="Choose a bundle product">',
      '<div class="advanced-subscription-modal__header">',
      '<h3>Choose a product</h3>',
      '<button type="button" class="advanced-subscription-modal__close" data-advanced-subscription-modal-close aria-label="Close">×</button>',
      '</div>',
      '<div class="advanced-subscription-modal__grid" data-advanced-subscription-modal-grid></div>',
      '</div>',
    ].join("");

    document.body.appendChild(bundleModal);
    bundleModal.addEventListener("click", (event) => {
      if (event.target.closest("[data-advanced-subscription-modal-close]")) {
        closeBundleModal();
        return;
      }

      const option = event.target.closest("[data-advanced-subscription-product-option]");
      if (!option) {
        return;
      }

      const productOption = bundleProducts.find(
        (item) => String(item.variantId) === option.dataset.advancedSubscriptionProductOption
      );
      if (productOption && activeBundleSlot != null) {
        if (activeBundleSlot === 0) {
          firstSlotWasRemoved = false;
        }
        bundleSlots[activeBundleSlot] = productOption;
        renderBundleSlots();
        refreshSubscriptionPricing();
      }
      closeBundleModal();
    });

    return bundleModal;
  };

  const closeBundleModal = () => {
    if (!bundleModal) {
      return;
    }
    bundleModal.hidden = true;
    document.documentElement.classList.remove("advanced-subscription-modal-open");
  };

  const openBundleModal = (slotIndex) => {
    activeBundleSlot = slotIndex;
    const modal = createBundleModal();
    const grid = modal.querySelector("[data-advanced-subscription-modal-grid]");
    grid.replaceChildren();

    bundleProducts.forEach((item) => {
      if (!item.available || !item.variantId) {
        return;
      }

      const button = document.createElement("button");
      button.type = "button";
      button.className = "advanced-subscription-modal__product";
      button.dataset.advancedSubscriptionProductOption = String(item.variantId);

      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = item.title || "";
        image.loading = "lazy";
        button.appendChild(image);
      }

      const title = document.createElement("span");
      title.className = "advanced-subscription-modal__product-title";
      title.textContent = item.title || "Product";
      button.appendChild(title);

      const price = document.createElement("span");
      price.className = "advanced-subscription-modal__product-price";
      price.textContent = formatBundleMoney(item.price || 0);
      button.appendChild(price);

      grid.appendChild(button);
    });

    modal.hidden = false;
    document.documentElement.classList.add("advanced-subscription-modal-open");
  };

  const updateOneTimeVolumePrice = () => {
    const basePrice = Number(currentVariant?.price || 0);
    const variantComparePrice = Number(currentVariant?.compare_at_price || 0);
    const comparePrice = variantComparePrice > basePrice ? variantComparePrice : null;

    setNodeHtml(oneTimePriceValue || oneTimePrice, formatAdvancedSubscriptionMoney(basePrice, moneyFormat));
    setNodeHtml(
      oneTimeCompareValue || oneTimeCompare,
      comparePrice ? formatAdvancedSubscriptionMoney(comparePrice, moneyFormat) : ""
    );

    if (oneTimeCompare) {
      oneTimeCompare.hidden = !comparePrice;
    }
    if (oneTimeDiscountBadge) {
      oneTimeDiscountBadge.textContent = "";
      oneTimeDiscountBadge.hidden = true;
    }
    if (oneTimeVolumeBadges) {
      syncOneTimeVolumeBadges();
    }
    if (oneTimeVolumeTiles) {
      syncOneTimeVolumeTiles();
    }
    renderBundleSlots();
  };

  const updatePrices = (allocation) => {
    const basePrice = currentVariant?.price || 0;
    updateOneTimeVolumePrice();
    if (!subscriptionPrice) {
      return;
    }

    if (!allocation) {
      const basePriceHtml = formatAdvancedSubscriptionMoney(basePrice, moneyFormat);
      if (subscriptionPriceValue) {
        subscriptionPriceValue.innerHTML = basePriceHtml;
      } else {
        subscriptionPrice.innerHTML = basePriceHtml;
      }
      if (subscriptionCompare) {
        if (subscriptionCompareValue) {
          subscriptionCompareValue.textContent = "";
        } else {
          subscriptionCompare.textContent = "";
        }
      }
      return;
    }

    const selectedItems = getSelectedBundleItems();
    const hasSubscriptionSelection = selectedItems.length > 0;
    let comparePrice = allocation.compare_at_price || basePrice;
    let price = allocation.price || basePrice;
    let discount =
      comparePrice > price
        ? Math.round(((comparePrice - price) / comparePrice) * 100)
        : 0;

    if (hasSubscriptionSelection) {
      comparePrice = getBundleBaseTotal() || comparePrice;
      discount = getSubscriptionDiscountPercent();
      price = discount > 0
        ? Math.round(comparePrice * (100 - discount) / 100)
        : comparePrice;
    }

    const subscriptionPriceHtml = formatAdvancedSubscriptionMoney(price, moneyFormat);
    if (subscriptionPriceValue) {
      subscriptionPriceValue.innerHTML = subscriptionPriceHtml;
    } else {
      subscriptionPrice.innerHTML = subscriptionPriceHtml;
    }
    if (subscriptionCompare) {
      const comparePriceHtml =
        discount > 0 ? formatAdvancedSubscriptionMoney(comparePrice, moneyFormat) : "";
      if (subscriptionCompareValue) {
        subscriptionCompareValue.innerHTML = comparePriceHtml;
      } else {
        subscriptionCompare.innerHTML = comparePriceHtml;
      }
    }
  };

  const setSubscriptionSelected = (isSubscription) => {
    if (subscriptionOption) {
      subscriptionOption.classList.toggle("is-active", isSubscription);
    }
    if (oneTimeOption) {
      oneTimeOption.classList.toggle("is-active", !isSubscription);
    }
    container.classList.toggle("is-subscription", isSubscription);
    if (planInput) {
      planInput.value = isSubscription && selectedPlanId ? String(selectedPlanId) : "";
    }
  };

  const getPlanDisplayName = (plan, planId) =>
    plan?.name
      ? plan.name.replace(/deliv[^\s]*/gi, "").trim()
      : `Plan ${planId}`;

  const getPlanDeliveryKey = (plan) =>
    (plan?.options?.[0]?.value || plan?.name || "").trim().toLowerCase();

  const getPlanRenewalDiscountPercent = (plan) => {
    const adjustments = plan?.price_adjustments || [];
    const recurringAdjustment = adjustments.find((adjustment) => adjustment.order_count == null);
    const adjustment = recurringAdjustment || adjustments[adjustments.length - 1];
    return Number(adjustment?.value || 0);
  };

  const isHiddenPlanOption = (allocation) => {
    const planId = Number(allocation.selling_plan_id);
    const plan = sellingPlanMap[allocation.selling_plan_id];
    return /^every month$/i.test(getPlanDisplayName(plan, planId));
  };

  // Asana 1219945312378906: Admin billing/delivery/pricing policies verified
  // equivalent on 2026-10-27. Do not infer equivalence for other IDs from labels.
  const equivalentPlanPairs = [
    [1727955077, 1728151685],
    [1727987845, 1728184453],
    [1728020613, 1728217221],
    [1728053381, 1728249989],
    [1728086149, 1728282757],
    [1728118917, 1728315525],
  ];

  const deduplicateVerifiedPlanAllocations = (allocations) => {
    const result = allocations.slice();
    equivalentPlanPairs.forEach((pair) => {
      const matches = result.filter((allocation) => pair.includes(Number(allocation.selling_plan_id)));
      if (matches.length !== 2) return;
      const signatures = matches.map((allocation) => {
        const plan = sellingPlanMap[allocation.selling_plan_id];
        if (!plan) return null;
        // Selection state and IDs are not economic terms. Keep all other
        // storefront fields in the comparison so observed changes fail open.
        const { id, selected, ...planTerms } = plan;
        const { selling_plan_id, ...allocationTerms } = allocation;
        return JSON.stringify([planTerms, allocationTerms]);
      });
      if (!signatures[0] || signatures[0] !== signatures[1]) return;
      const retained = matches.find((allocation) => Number(allocation.selling_plan_id) === selectedPlanId) || matches[0];
      // Preserve the original frequency order even when retaining the second ID.
      result[result.indexOf(matches[0])] = retained;
      result.splice(result.lastIndexOf(matches[1]), 1);
    });
    return result;
  };

  const getVisiblePlanAllocations = () => {
    const visibleAllocations = currentAllocations.filter((allocation) => !isHiddenPlanOption(allocation));
    const renewalDiscount = getSubscriptionRenewalDiscountPercent();
    const tierAllocations = visibleAllocations.filter((allocation) =>
      getPlanRenewalDiscountPercent(sellingPlanMap[allocation.selling_plan_id]) === renewalDiscount
    );
    if (tierAllocations.length) {
      return deduplicateVerifiedPlanAllocations(tierAllocations);
    }
    return deduplicateVerifiedPlanAllocations(visibleAllocations.length ? visibleAllocations : currentAllocations);
  };

  const syncSelectedPlanToBundleTier = () => {
    if (!currentAllocations.length) {
      selectedPlanId = null;
      return;
    }

    const renewalDiscount = getSubscriptionRenewalDiscountPercent();
    const currentDeliveryKey = getPlanDeliveryKey(sellingPlanMap[selectedPlanId]);
    const matchingAllocations = currentAllocations.filter((allocation) => {
      const plan = sellingPlanMap[allocation.selling_plan_id];
      return (
        !isHiddenPlanOption(allocation) &&
        getPlanRenewalDiscountPercent(plan) === renewalDiscount
      );
    });
    const selectedAllocation =
      matchingAllocations.find((allocation) => Number(allocation.selling_plan_id) === selectedPlanId) ||
      matchingAllocations.find(
        (allocation) =>
          getPlanDeliveryKey(sellingPlanMap[allocation.selling_plan_id]) === currentDeliveryKey
      ) || matchingAllocations[0];

    if (selectedAllocation) {
      selectedPlanId = Number(selectedAllocation.selling_plan_id);
    }
  };

  const refreshSubscriptionPricing = () => {
    syncSelectedPlanToBundleTier();
    renderPlanOptions();
    updatePrices(
      currentAllocations.find(
        (allocation) => Number(allocation.selling_plan_id) === selectedPlanId
      )
    );
    setSubscriptionSelected(subscriptionRadio ? subscriptionRadio.checked : true);
    syncStickyFormState();
  };

  const renderPlanOptions = () => {
    if (!planSelect) {
      return;
    }
    planSelect.innerHTML = "";
    getVisiblePlanAllocations().forEach((allocation) => {
      const planId = Number(allocation.selling_plan_id);
      const plan = sellingPlanMap[allocation.selling_plan_id];
      const option = document.createElement("option");
      option.value = String(planId);
      option.textContent = getPlanDisplayName(plan, planId);
      option.selected = planId === selectedPlanId;
      planSelect.appendChild(option);
    });
  };

  const updateVariant = () => {
    const variantId = Number(form.querySelector('input[name="id"]')?.value);
    currentVariant = getVariantById(variantId) || (product.variants || [])[0];
    if (!currentVariant) {
      return;
    }

    currentAllocations = (currentVariant.selling_plan_allocations || []).filter((allocation) =>
      sellingPlanIds.has(allocation.selling_plan_id)
    );

    container.classList.remove("advanced-subscription--hidden");
    container.classList.toggle("advanced-subscription--one-time-only", !currentAllocations.length);

    if (!currentAllocations.length) {
      selectedPlanId = null;
      if (planInput) {
        planInput.value = "";
      }
      if (subscriptionRadio && oneTimeRadio) {
        subscriptionRadio.checked = false;
        oneTimeRadio.checked = true;
      }
      renderPlanOptions();
      updatePrices(null);
      setSubscriptionSelected(false);
      syncStickyFormState();
      initialized = true;
      return;
    }

    const visibleAllocations = getVisiblePlanAllocations();

    if (
      !selectedPlanId ||
      !visibleAllocations.find(
        (allocation) => Number(allocation.selling_plan_id) === selectedPlanId
      )
    ) {
      selectedPlanId = Number(visibleAllocations[0].selling_plan_id);
    }

    refreshSubscriptionPricing();

    if (!initialized) {
      const defaultSelection = settings?.default_subscription === "subscription";
      if (subscriptionRadio && oneTimeRadio) {
        subscriptionRadio.checked = defaultSelection;
        oneTimeRadio.checked = !defaultSelection;
      }
      setSubscriptionSelected(defaultSelection);
      initialized = true;
    } else {
      setSubscriptionSelected(subscriptionRadio ? subscriptionRadio.checked : true);
    }

    syncStickyFormState();
  };

  if (subscriptionRadio) {
    subscriptionRadio.addEventListener("change", () => {
      setSubscriptionSelected(true);
      syncStickyFormState();
    });
  }
  if (oneTimeRadio) {
    oneTimeRadio.addEventListener("change", () => {
      setSubscriptionSelected(false);
      syncStickyFormState();
    });
  }
  if (subscriptionOption && subscriptionRadio) {
    subscriptionOption.addEventListener("click", (event) => {
      if (event.target.closest("[data-advanced-subscription-slot-remove]")) {
        return;
      }

      if (!subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
        subscriptionRadio.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        setSubscriptionSelected(true);
      }
    });
  }
  if (oneTimeOption && oneTimeRadio) {
    oneTimeOption.addEventListener("click", (event) => {
      if (
        event.target.closest("[data-advanced-subscription-slot]") ||
        event.target.closest("[data-advanced-subscription-slot-remove]")
      ) {
        return;
      }

      if (!oneTimeRadio.checked) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        setSubscriptionSelected(false);
      }
    });
  }
  if (planSelect) {
    planSelect.addEventListener("change", (event) => {
      selectedPlanId = Number(event.currentTarget.value);
      refreshSubscriptionPricing();
    });
  }

  container.addEventListener("click", (event) => {
    const removeButton = event.target.closest("[data-advanced-subscription-slot-remove]");
    if (removeButton && container.contains(removeButton)) {
      const slotIndexes = (removeButton.dataset.advancedSubscriptionSlotRemoveIndexes || "")
        .split(",")
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => Number.isFinite(value));
      const slotQuantity = Number.parseInt(
        removeButton.dataset.advancedSubscriptionSlotRemoveQuantity || "1",
        10
      );
      const duplicateIndexes = slotIndexes.filter((value) => value !== 0);
      const quantityInput = form.querySelector('input[name="quantity"]');

      if (duplicateIndexes.length) {
        const slotIndex = duplicateIndexes[duplicateIndexes.length - 1];
        event.preventDefault();
        event.stopPropagation();
        bundleSlots[slotIndex] = null;
        renderBundleSlots();
        refreshSubscriptionPricing();
        return;
      }

      if (
        slotIndexes.includes(0) &&
        slotQuantity > 1 &&
        quantityInput
      ) {
        event.preventDefault();
        event.stopPropagation();
        quantityInput.value = String(Math.max(getProductFormQuantity() - 1, 1));
        quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }

      const slotIndex = slotIndexes.length
        ? slotIndexes[slotIndexes.length - 1]
        : Number.parseInt(
            removeButton.closest("[data-advanced-subscription-slot]")?.dataset
              .advancedSubscriptionSlot || "0",
            10
          );

      if (Number.isFinite(slotIndex) && bundleSlots[slotIndex]) {
        event.preventDefault();
        event.stopPropagation();
        if (slotIndex === 0) {
          firstSlotWasRemoved = true;
        }
        bundleSlots[slotIndex] = null;
        renderBundleSlots();
        refreshSubscriptionPricing();
      }
      return;
    }

    const tile = event.target.closest("[data-advanced-subscription-slot]");
    if (!tile || !container.contains(tile)) {
      return;
    }

    event.preventDefault();
    const slotIndex = Number.parseInt(tile.dataset.advancedSubscriptionSlot || "0", 10);
    if (!Number.isFinite(slotIndex)) {
      return;
    }

    openBundleModal(slotIndex);
  });

  container.addEventListener("keydown", (event) => {
    const removeButton = event.target.closest?.("[data-advanced-subscription-slot-remove]");
    if (!removeButton || !container.contains(removeButton)) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      removeButton.click();
    }
  });

  form.addEventListener("submit", submitBundleCart, true);

  form.addEventListener("change", (event) => {
    if (event.target?.matches?.('input[name="quantity"]')) {
      refreshSubscriptionPricing();
    }
  });

  form.addEventListener("input", (event) => {
    if (event.target?.matches?.('input[name="quantity"]')) {
      refreshSubscriptionPricing();
    }
  });

  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput) {
    variantInput.addEventListener("change", updateVariant);
  }

  if (stickyForm) {
    stickyForm.addEventListener("submit", submitMainProductFormFromSticky, true);
  }

  updateVariant();
};

const initAdvancedSubscriptionSelectors = (root = document) => {
  root.querySelectorAll(ADVANCED_SUBSCRIPTION_SELECTOR).forEach((container) => {
    initAdvancedSubscription(container);
  });
};

const setupAdvancedSubscriptionFocusMode = () => {
  if (document.documentElement.dataset.advancedSubscriptionFocusModeAttached === "true") {
    return;
  }

  document.documentElement.dataset.advancedSubscriptionFocusModeAttached = "true";

  document.addEventListener("keydown", (event) => {
    if (event.key === "Tab") {
      document.documentElement.classList.add("advanced-subscription-keyboard-focus");
    }
  }, true);

  document.addEventListener("pointerdown", () => {
    document.documentElement.classList.remove("advanced-subscription-keyboard-focus");
  }, true);
};

setupAdvancedSubscriptionFocusMode();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initAdvancedSubscriptionSelectors());
} else {
  initAdvancedSubscriptionSelectors();
}

document.addEventListener("shopify:section:load", (event) =>
  initAdvancedSubscriptionSelectors(event.target)
);
